import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { nativePrintTarget, resolvePrinterForElectron } from '../utils/printTarget';
import { formatPrice } from '../utils/formatPrice';
import { getSeatToPay } from '../utils/splitTotals';
import { StyleSheet } from 'react-native';
import { Dimensions } from 'react-native';
import ReceiptPopup from '../components/ReceiptPopup';
import { Product, Category, OrderProduct, ProductVariant, OrderProductAddon, Order, ProductAddon, Floor, Combination, OrderCombination, Table, ProstayGuestDto, Customer, CustomerDiscount } from '../interfaces';
import AddVariantsPopup from '../components/AddVariantsPopup';
import AddAddonsPopup from '../components/AddAddonsPopup';
import TableActionsMenu from '../components/popups/TableActionsMenu';
import AddDiscountPopup from '../components/AddDiscountPopup';
import AddDiscountValue from '../components/AddDiscountValue';
import AddProductNotePopup from '../components/AddProductNotePopup';
import EditAddonsForProduct from '../components/EditAddonsForProduct';
import EditVariantsForProduct from '../components/EditVariantsForProduct';
import { useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import AddOrderDiscountValue from '../components/AddOrderDiscountValue';
import SwipeableOrderItem from '../components/SwipeableOrderItem';
import SwipeableCombinationItem from '../components/SwipeableCombinationItem';
import './TableOrder.css';
import '../components/itemActions.css';
import Spinner from '../components/Spinner';
import AddCombinationDiscountPopup from '../components/AddCombinationDiscountPopup';
import CashPaymentPopup from '../components/CashPaymentPopup';
import CardPaymentPopup from '../components/CardPaymentPopup';
import SuccessNotification from '../components/notifications/SuccessNotification';
import ErrorNotification from '../components/notifications/ErrorNotification';
import DeleteConfirmation from '../components/DeleteConfirmationPopup';
import ChangeTablePopup from '../components/popups/ChangeTablePopup';
import ChangeTableConfirmationPopup from '../components/popups/ChangeTableConfirmationPopup';
import { useNavigate } from 'react-router-dom';
import SendProductsPopup from '../components/popups/SendProductsPopup';
import AddDiscountForOrderItem from '../components/AddDiscountForOrderItem';
import EditOrderItemPopup from '../components/popups/EditOrderItemPopup';
import DeleteFromOrderItem from '../components/popups/DeleteFromOrderItem';
import AddFlexibleCombinationPopup from '../components/popups/AddFlexibleCombinationPopup';
import EditFlexibleCombinationPopup from '../components/popups/EditFlexibleCombinationPopup';
import Toolbar, { CombinedSearchResult } from '../Toolbar';
import SelectReservationPopup from '../components/SelectReservationPopup';
import { Seat, SeatMealPlanResult, MealPlanConfig } from '../interfaces/mealPlan';
import TableSetupDialog from '../components/meal-plan/TableSetupDialog';
import ReservationService from '../services/ReservationService';
import { SeatingHandoff, clearSeatingHandoff, getSeatingHandoff } from '../services/reservationSeating';
import { GUEST_CHECKED_OUT_TITLE, GUEST_CHECKED_OUT_TOAST_MS, readRoomChargeFailure } from '../services/roomChargeRefusal';
import SeatSelector from '../components/meal-plan/SeatSelector';
import AllowanceTracker from '../components/meal-plan/AllowanceTracker';
import ExtraWarning from '../components/meal-plan/ExtraWarning';
import MealPlanOverridePopup from '../components/meal-plan/MealPlanOverridePopup';
import { useMealPlanEngine, getItemMealCategoryFromCategoryName } from '../hooks/useMealPlanEngine';
import { getRef, putRef, putOrder as putOrderToDb } from '../services/OfflineDb';
import OrderClosedPopup from '../components/popups/OrderClosedPopup';



const loadSelectedFloorId = (setSelectedFloorId: (id: string) => void) => {
    const savedFloor = localStorage.getItem('selectedFloor');
    if (savedFloor) {
        const parsedFloor = JSON.parse(savedFloor);
        setSelectedFloorId(parsedFloor.value);
    }
};

// Local calendar date ("YYYY-MM-DD"). Meal allowances are keyed by it and the API records them
// on order close, so the date has to be the one the POS sees — never the UTC date.
const localDateString = (): string => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const TableOrder = () => {
    const { hasPerm } = useAuth();
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [products, setProducts] = useState<Product[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [favorites, setFavorites] = useState<Product[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<Category>();
    const [orderProducts, setOrderProducts] = useState<OrderProduct[]>([]);
    const [isReceiptPopupOpen, setIsReceiptPopupOpen] = useState(false);
    const [isCashPopupOpen, setisCashPopupOpen] = useState(false);
    const [isCardPopupOpen, setisCardPopupOpen] = useState(false);
    const [splitPaySeatTotal, setSplitPaySeatTotal] = useState<number | undefined>(undefined);
    const [paymentReservationInfo, setPaymentReservationInfo] = useState<{ reservationId: string; reservationNumber: string; roomNumber: string; roomWithStatusId: string; seatId?: string; guestName?: string } | undefined>(undefined);
    const [showWhoPaysPopup, setShowWhoPaysPopup] = useState(false);
    const [pendingPaymentMethod, setPendingPaymentMethod] = useState<'cash' | 'card' | 'room' | null>(null);
    const [capturedTotal, setCapturedTotal] = useState<number>(0);
    const [isPutOnRoomOpen, setIsPutOnRoomOpen] = useState(false);
    const [selectedProductToAdd, setSelectedProductToAdd] = useState<Product | null>(null);
    const [isAddVariantOpen, setIsAddVariantOpen] = useState(false);
    const [isAddAddonOpen, setIsAddAddonOpen] = useState(false);
    const [isOrderOptionsOpen, setIsOrderOptionsOpen] = useState(false);
    const [isOrderCombinationsOptionsOpen, setIsOrderCombinationsOptionsOpen] = useState(false);
    const [optionsOpenedForCombinationIndex, setOptionsOpenedForCombinationIndex] = useState<number | null>(null);
    const [longPressTimeout, setLongPressTimeout] = useState<NodeJS.Timeout | null>(null);
    const [isTableActionsPopupOpen, setIsTableActionsPopupOpen] = useState(false);
    const [isAddDiscountPopupOpen, setIsAddDiscountPopupOpen] = useState(false);
    const [isAddDiscountValuePopupOpen, setIsAddDiscountValuePopupOpen] = useState(false);
    const [addingDiscountForProduct, setAddingDiscountForProduct] = useState<OrderProduct | null>(null);
    const [addingDiscountForCombination, setAddingDiscountForCombination] = useState<OrderCombination | null>(null);
    const [addingDiscountForProductIndex, setAddingDiscountForProductIndex] = useState<number | null>(null);
    const [addingDiscountForCombinationIndex, setAddingDiscountForCombinationIndex] = useState<number | null>(null);
    const [isAddNotePopupOpen, setIsAddNotePopupOpen] = useState(false);
    const [addingAddonForProduct, setAddingAddonForProduct] = useState<OrderProduct | null>(null);
    const [addingVariantForProduct, setAddingVariantForProduct] = useState<OrderProduct | null>(null);
    const [activeOrder, setActiveOrder] = useState<Order>();
    const [isOrderDiscountOpen, setisOrderDiscountOpen] = useState(false);
    const [totalHeight, setTotalHeight] = useState<number | null>(null);
    const [emptyRowCount, setEmptyRowCount] = useState<number>(0);
    const [floor, setFloor] = useState<Floor | null>(null);
    const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
    const [combinations, setCombinations] = useState<Combination[]>([]);
    const [isCombinationsSelected, setIsCombinationsSelected] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    // The initial catalog/order loads must NOT share `showSpinner` with the ad-hoc action
    // handlers: several of those run concurrently on mount (the floor fetch, for one) and
    // whichever finished first used to flip the shared flag off, hiding the spinner while
    // products were still in flight — leaving an empty screen with no indication of loading.
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [catalogFailed, setCatalogFailed] = useState(false);
    const [catalogReloadKey, setCatalogReloadKey] = useState(0);
    const [orderLoading, setOrderLoading] = useState(true);
    const [successTitle, setSuccessTitle] = useState('');
    const [successContent, setSuccessContent] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [emptyTables, setEmptyTables] = useState<Table[] | null>(null);
    const [isChangeTablePopupOpen, setIsChangeTablePopupOpen] = useState(false);
    const [isChangeTableConfirmationOpen, setIsChangeTableConfirmationOpen] = useState(false);
    const [selectedNewTableId, setSelectedNewTableId] = useState('');
    const [selectedNewTableName, setSelectedNewTableName] = useState('');
    const navigate = useNavigate();
    const [isSendProductsPopupOpen, setIsSendProductsPopupOpen] = useState(false);
    const [isDiscountPopupOpenForItems, setIsDiscountPopupOpenForItems] = useState(false);
    const [addingFlexibleCombination, setAddingFlexibleCombination] = useState<Combination | null>(null);
    const [searchMenuOpen, setSearchMenuOpen] = useState<boolean>(false);
    const [errorTitle, setErrorTitle] = useState('');
    const [errorContent, setErrorContent] = useState('');
    const [showError, setShowError] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isPrintRowOpen, setIsPrintRowOpen] = useState(false);
    const [showDeleteOrderConfirmation, setShowDeleteOrderConfirmation] = useState(false);
    const [orderClosedInfo, setOrderClosedInfo] = useState<{ closedBy: string; closedAt: string } | null>(null);
    const [orderViewers, setOrderViewers] = useState<{ displayName: string; userName: string }[]>([]);

    // ─── Seat & Meal Plan State ───────────────────────────────────────────────
    const [seats, setSeats] = useState<Seat[]>([]);
    const [linkedReservationNumber, setLinkedReservationNumber] = useState<string>('');
    const [linkedRoomNumber, setLinkedRoomNumber] = useState<string>('');
    const [activeSeatId, setActiveSeatId] = useState<string | null>(null);
    const [showTableSetup, setShowTableSetup] = useState(false);
    const [pendingDialogGuest, setPendingDialogGuest] = useState<CombinedSearchResult | null>(null);

    /** Set when we arrived here from "Seat" on a floor-plan reservation. */
    const [seatingHandoff, setSeatingHandoff] = useState<SeatingHandoff | null>(null);

    // The dialog already knows how to consume a search result, so express the booked guest as
    // one rather than teaching it a second shape.
    const seatingPendingGuest = useMemo<CombinedSearchResult | null>(() => {
        if (!seatingHandoff) return null;
        const [firstName, ...rest] = (seatingHandoff.guestName || 'Guest').split(/\s+/);
        const lastName = rest.join(' ');
        if (seatingHandoff.prostayReservationId) {
            return {
                type: 'prostay',
                firstName,
                lastName,
                email: seatingHandoff.guestEmail,
                roomNumber: seatingHandoff.roomNumber,
                reservationNumber: seatingHandoff.prostayReservationNumber,
                id: seatingHandoff.prostayGuestId,
                prostayGuest: {
                    id: seatingHandoff.prostayGuestId || '',
                    firstName,
                    lastName,
                    email: seatingHandoff.guestEmail,
                    reservationId: seatingHandoff.prostayReservationId,
                    reservationNumber: seatingHandoff.prostayReservationNumber,
                    roomNumber: seatingHandoff.roomNumber,
                    isRepeat: false,
                    repeatGuests: false,
                } as ProstayGuestDto,
            } as CombinedSearchResult;
        }
        return {
            type: 'tableview',
            firstName,
            lastName,
            email: seatingHandoff.guestEmail,
            id: seatingHandoff.customerId,
            customer: seatingHandoff.customerId
                ? ({ id: seatingHandoff.customerId, firstName, lastName, email: seatingHandoff.guestEmail } as Customer)
                : undefined,
        } as CombinedSearchResult;
    }, [seatingHandoff]);
    const [_tableSetupChecked, setTableSetupChecked] = useState(false);
    const [seatResults, setSeatResults] = useState<Record<string, SeatMealPlanResult>>({});
    const [extraWarning, setExtraWarning] = useState<{ message: string; roomNumber?: string } | null>(null);
    const [pendingOverrideProduct, setPendingOverrideProduct] = useState<{ product: OrderProduct; extraReason: string; guestName?: string; roomNumber?: string; sourceProduct?: Product } | null>(null);

    // ─── Order Presence Heartbeat ────────────────────────────────────────────
    useEffect(() => {
        if (!activeOrder?.id) return;

        const orderId = activeOrder.id;

        const sendHeartbeat = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/Order/heartbeat/${orderId}`, {
                    method: 'POST',
                    credentials: 'include',
                });
                if (res.ok) {
                    const viewers = await res.json();
                    setOrderViewers(viewers);
                }
            } catch {
                // Ignore heartbeat failures
            }
        };

        sendHeartbeat();
        const interval = setInterval(sendHeartbeat, 20000);

        const leaveUrl = `${process.env.REACT_APP_API_URL}/Order/leave/${orderId}`;

        return () => {
            clearInterval(interval);
            // Use keepalive fetch to ensure the request completes even during page unload
            fetch(leaveUrl, {
                method: 'POST',
                credentials: 'include',
                keepalive: true,
            }).catch(() => {});
        };
    }, [activeOrder?.id]);

    // Keep activeOrder.products in sync with orderProducts, stamp meal plan status
    useEffect(() => {
        setActiveOrder(prev => {
            // Note: do NOT bail when orderProducts is empty — an order emptied by
            // deleting its last item must be mirrored here too, otherwise activeOrder
            // keeps the stale last item and a later save can re-persist it.
            if (!prev) return prev;
            const stamped = orderProducts.map(p => {
                // Use live seatResults if available, otherwise preserve saved mealPlanStatus from DB
                let mealPlanStatus: string = (p as any).mealPlanStatus || 'none';
                if (p.seatId && seatResults[p.seatId]) {
                    const eval_ = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id);
                    if (eval_) mealPlanStatus = eval_.status;
                }
                return { ...p, mealPlanStatus };
            });
            return { ...prev, products: stamped };
        });
    }, [orderProducts, seatResults]);

    // Debounced auto-save after meal plan stamps change so the DB always has current mealPlanStatus
    const stampSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!activeOrder || orderProducts.length === 0) return;
        if (!seats.some(s => s.type === 'hotel_guest')) return;
        // Only if seatResults have evaluations (stamps are meaningful)
        const hasEvals = Object.values(seatResults).some(r => r.evaluations && r.evaluations.length > 0);
        if (!hasEvals) return;

        if (stampSaveTimerRef.current) clearTimeout(stampSaveTimerRef.current);
        stampSaveTimerRef.current = setTimeout(() => {
            if (activeOrder) saveOrder(activeOrder);
        }, 2000);

        return () => { if (stampSaveTimerRef.current) clearTimeout(stampSaveTimerRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seatResults]);

    const [_mealPeriodOverride] = useState<string | null>(null);
    const [seatConfigs, setSeatConfigs] = useState<Record<string, MealPlanConfig | null>>({});

    const mealPlanEngine = useMealPlanEngine();

    // Fetch meal plan configs and prefetch consumption status when seats change
    useEffect(() => {
        const fetchConfigsAndPrefetch = async () => {
            // Always force-refresh configs so changes in prostay are picked up
            const newConfigs: Record<string, MealPlanConfig | null> = {};
            for (const seat of seats) {
                if (seat.rateId && newConfigs[seat.rateId] === undefined) {
                    const config = await mealPlanEngine.fetchMealPlanConfig(seat.rateId, true);
                    newConfigs[seat.rateId] = config;
                }
            }
            // Prefetch consumption status BEFORE setting configs,
            // so the recalc that fires from setSeatConfigs uses fresh consumption data
            if (seats.some(s => s.type === 'hotel_guest')) {
                await mealPlanEngine.prefetchConsumptionStatus(seats, newConfigs);
            }
            setSeatConfigs(newConfigs);

            // Backfill the capture stamps on hotel-guest seats that don't have them: seats set up
            // before their rate's config had been fetched (the configs load from this very effect,
            // so the first seat setup of a session always lands without a period), and seats added
            // by attaching a guest to the order, which never stamped anything. Without them the
            // seat closes with nothing identifying which allowance it spent.
            let changed = false;
            const stamped = seats.map(s => {
                if (s.type !== 'hotel_guest') return s;
                const next = { ...s };
                let seatChanged = false;
                if (!next.capturedDate) { next.capturedDate = localDateString(); seatChanged = true; }
                if (!next.capturedMealPeriod && next.rateId) {
                    const period = mealPlanEngine.getCurrentMealPeriod(newConfigs[next.rateId] || null);
                    if (period) { next.capturedMealPeriod = period; seatChanged = true; }
                }
                if (!seatChanged) return s;
                changed = true;
                return next;
            });
            if (changed) {
                setSeats(stamped);
                // Keep the order's own copy in step — the payment popups close the order from
                // activeOrder.seats, and the API reads the capture stamps off that list.
                setActiveOrder(prev => (prev ? { ...prev, seats: stamped } : prev));
            }
        };
        if (seats.some(s => s.rateId)) {
            fetchConfigsAndPrefetch();
        } else if (seats.some(s => s.type === 'hotel_guest')) {
            mealPlanEngine.prefetchConsumptionStatus(seats).then(() => {
                // Force recalc after prefetch by toggling seatConfigs
                setSeatConfigs(prev => ({ ...prev }));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seats]);

    // ─── Seat-based filtering ─────────────────────────────────────────────────
    const itemCountPerSeat = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const seat of seats) {
            counts[seat.id] = orderProducts.filter(p => p.seatId === seat.id).length +
                (activeOrder?.combinations?.filter(c => c.seatId === seat.id).length || 0);
        }
        return counts;
    }, [seats, orderProducts, activeOrder?.combinations]);

    // Recalculate meal plan results when items change
    const computeMealPlanResults = useCallback(() => {
        const newResults: Record<string, SeatMealPlanResult> = {};
        for (const seat of seats) {
            const config = seat.rateId ? (seatConfigs[seat.rateId] || null) : null;
            const seatItems = orderProducts
                .filter(p => p.seatId === seat.id)
                .map(p => {
                    const cat = categories.find(c => c.id === p.categoryId);
                    return { ...p, categoryName: cat?.name || p.categoryName || '' };
                });
            newResults[seat.id] = mealPlanEngine.evaluateSeatItems(seat, seatItems, config);
        }
        return newResults;
    }, [seats, orderProducts, categories, mealPlanEngine, seatConfigs]);

    const recalcMealPlanResults = useCallback(() => {
        setSeatResults(computeMealPlanResults());
    }, [computeMealPlanResults]);

    useEffect(() => {
        if (seats.length > 0) {
            recalcMealPlanResults();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seats.length, orderProducts, seatConfigs]);

    // Re-evaluate meal plan when a meal period boundary is crossed
    useEffect(() => {
        if (seats.length === 0) return;

        // Find the next meal period boundary (closest end time in the future)
        let nextBoundaryMs: number | null = null;
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();

        for (const seat of seats) {
            const config = seat.rateId ? (seatConfigs[seat.rateId] || null) : null;
            if (!config) continue;
            for (const period of config.mealPeriods) {
                if (!period.isEnabled) continue;
                const [eh, em] = period.timeWindowEnd.split(':').map(Number);
                const endMin = eh * 60 + em;
                if (endMin > nowMin) {
                    const diffMs = (endMin - nowMin) * 60 * 1000 - (now.getSeconds() * 1000);
                    if (nextBoundaryMs === null || diffMs < nextBoundaryMs) {
                        nextBoundaryMs = diffMs;
                    }
                }
            }
        }

        if (nextBoundaryMs !== null && nextBoundaryMs > 0) {
            const timer = setTimeout(() => {
                recalcMealPlanResults();
            }, nextBoundaryMs + 1000); // +1s buffer
            return () => clearTimeout(timer);
        }
    }, [seats, seatConfigs, recalcMealPlanResults]);

    // ─── End Seat & Meal Plan State ───────────────────────────────────────────

    // const [searchResults, setSearchResults] = useState<ProstayGuestDto[]>([]);
    // const [searchGuestTerm, setSearchGuestTerm] = useState('');
    // const searchRef = useRef<HTMLDivElement>(null); 

    // useEffect(() => {
    //     const handleClickOutside = (event: MouseEvent) => {
    //         if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
    //             setSearchMenuOpen(false);
    //         }
    //     };
    //     document.addEventListener("mousedown", handleClickOutside);
    //     return () => {
    //         document.removeEventListener("mousedown", handleClickOutside);
    //     };
    // }, []);

    // const [searchingGuests, setSearchingGuests] = useState(false);
    // const [, setRequestCounter] = useState(0); 

    // const handleGuestSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    //     const value = (event.target as HTMLInputElement).value;
    //     setSearchGuestTerm(value);

    //     if (value !== '') {
    //         setSearchMenuOpen(true);
    //     } else {
    //         setSearchMenuOpen(false);
    //     }

    //     fetchAutocompleteOptions(value);
    // };

    // const fetchAutocompleteOptions = async (searchValue: string) => {
    //     if (!searchValue) {
    //         setSearchResults([]);
    //         return;
    //     }

    //     const requestPayload = {
    //         currentPage: 1,
    //         searchTerm: searchValue,
    //         pageSize: 1000,
    //         filterDuplicatesByEmail: false
    //     };

    //     setSearchingGuests(true);
    //     setRequestCounter((prev) => prev + 1); 

    //     try {
    //         const response = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/search-prostay-guest`, {
    //             method: 'POST',
    //             headers: { 'Content-Type': 'application/json' },
    //             body: JSON.stringify(requestPayload),
    //             credentials: 'include',
    //         });

    //         if (!response.ok) {
    //             throw new Error('Failed to fetch guest data');
    //         }

    //         const data = await response.json();
    //         setSearchResults(data.data || []);
    //     } catch (error) {
    //         console.error('Error fetching guests:', error);
    //         setSearchResults([]);
    //     } finally {
    //         setRequestCounter((prev) => {
    //             const newCount = prev - 1;
    //             if (newCount === 0) {
    //                 setSearchingGuests(false); // Only hide spinner when all requests finish
    //             }
    //             return newCount;
    //         });
    //     }
    // };



    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);


    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);




    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'selectedFloor') {
                loadSelectedFloorId(setSelectedFloorId);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    useEffect(() => {
        loadSelectedFloorId(setSelectedFloorId);
    }, []);

    const fetchFloor = async (floorId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Floor/` + floorId, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Error fetching floor');
            }
            const data = await response.json();
            setFloor(data);
        } catch (error) {
            console.error("Error fetching data: ", error);
        }
    };

    const fetchAvailableEmptyTablesForFloor = async (floorId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Floor/get-all-empty-tables-for-floor-id/` + floorId, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Error fetching floor');
            }
            const data = await response.json();
            setEmptyTables(data);
        } catch (error) {
            console.error("Error fetching data: ", error);
        }
    };

    const getTableNoById = (): string | null => {
        if (activeOrder?.mergedTablesIds) {
            const tables = activeOrder.mergedTablesNames?.join(",");
            if (tables) {
                return tables;
            }
        }
        if (floor && floor.tables) {
            const table = floor.tables.find((t) => t.id === activeOrder?.tableId);
            return table ? table.tableNo : null;
        }
        return null;
    };

    // Height measurement handled via ref callback on order items div

    const { tableId } = useParams();

    // Seating a reservation from the floor plan parks its details in storage and navigates
    // here; pick them up so the setup dialog opens already knowing who is sitting down.
    useEffect(() => {
        setSeatingHandoff(tableId ? getSeatingHandoff(tableId) : null);
    }, [tableId]);

    // Anchor for the Table-actions menu (popover opens above this button).
    const tableActionsBtnRef = useRef<HTMLButtonElement>(null);

    // Restore seats from localStorage when tableId changes (including navigation between tables)
    useEffect(() => {
        try {
            const saved = localStorage.getItem(`seats_${tableId}`);
            if (saved) {
                const parsed = JSON.parse(saved);
                setSeats(parsed);
                setActiveSeatId(parsed.length > 0 ? parsed[0].id : null);
            } else {
                setSeats([]);
                setActiveSeatId(null);
            }
        } catch {
            setSeats([]);
            setActiveSeatId(null);
        }
        setTableSetupChecked(true);
        setShowTableSetup(false);
        setSeatResults({});
        setSeatConfigs({});
    }, [tableId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist seats to localStorage when they change
    useEffect(() => {
        if (seats.length > 0 && tableId) {
            localStorage.setItem(`seats_${tableId}`, JSON.stringify(seats));
        } else if (tableId) {
            localStorage.removeItem(`seats_${tableId}`);
        }
    }, [seats, tableId]);

    const handleLongPress = (productIndex: number) => {
        setLongPressTimeout(setTimeout(() => {
            const product = activeOrder?.products[productIndex];
            if (product) {
                const productId = product.id;
                setIsOrderOptionsOpen(true);

                setSelectedProductIds((prevSelectedProductIds) => {
                    if (!prevSelectedProductIds.includes(productId)) {
                        console.log("setting products")
                        console.log([...prevSelectedProductIds, productId])
                        return [...prevSelectedProductIds, productId];
                    }
                    return prevSelectedProductIds;
                });
            }
        }, 800));
    };

    const handleCombinationLongPress = (combinationIndex: number) => {
        setLongPressTimeout(setTimeout(() => {
            const combination = activeOrder?.combinations[combinationIndex];
            if (combination) {
                const combinationId = combination.id;
                setIsOrderCombinationsOptionsOpen(true);
                setOptionsOpenedForCombinationIndex(combinationIndex);
                console.log(optionsOpenedForCombinationIndex);
                setSelectedCombinationIds((prevSelectedCombinationIds) => {
                    if (!prevSelectedCombinationIds.includes(combinationId)) {
                        return [...prevSelectedCombinationIds, combinationId];
                    }
                    return prevSelectedCombinationIds;
                });
            }
        }, 800));
    };

    const areMultipleProductsSelected = (): boolean => {
        return (selectedCombinationIds != null ? selectedCombinationIds.length : 0) + (selectedProductIds != null ? selectedProductIds.length : 0) > 1;
    };

    const areAnyProductsSelected = (): boolean => {
        return (selectedCombinationIds != null ? selectedCombinationIds.length : 0) + (selectedProductIds != null ? selectedProductIds.length : 0) > 0;
    };

    const canEditSelectedItem = (): boolean => {
        const selectedProduct = selectedProductIds != null && selectedProductIds.length > 0 && activeOrder?.products != null && activeOrder?.products.length > 0 ? activeOrder?.products.find(x => x.id == selectedProductIds[0]) : undefined;
        const selectedCombi = selectedCombinationIds != null && selectedCombinationIds.length > 0 && activeOrder?.combinations != null && activeOrder?.combinations.length > 0 ? activeOrder?.combinations.find(x => x.id == selectedCombinationIds[0]) : undefined;

        if (selectedCombi != null && selectedCombi.type == 1) {
            return true;
        }

        if (selectedProduct != undefined) {
            if ((selectedProduct.addons != null && selectedProduct.addons.length > 0) || (selectedProduct.variants != null && selectedProduct.variants.length > 0) ||
                (selectedProduct.addons != null && selectedProduct.addons.length > 0) || (selectedProduct.note != null && selectedProduct.note != '') || (selectedProduct.discount != null && selectedProduct.discount != 0)) {
                return true;
            }
            else return false;
        }

        if (selectedCombi != undefined) {
            if ((selectedCombi.discount != null && selectedCombi.discount != 0) || (selectedCombi.note != null && selectedCombi.note != '')) {
                return true
            }
            else return false;
        }
        return false;
    }

    const canDeleteSelectedItem = (): boolean => {
        const selectedProduct = selectedProductIds != null && selectedProductIds.length > 0 && activeOrder?.products != null && activeOrder?.products.length > 0 ? activeOrder?.products.find(x => x.id == selectedProductIds[0]) : undefined;
        const selectedCombi = selectedCombinationIds != null && selectedCombinationIds.length > 0 && activeOrder?.combinations != null && activeOrder?.combinations.length > 0 ? activeOrder?.combinations.find(x => x.id == selectedCombinationIds[0]) : undefined;

        if (selectedProduct != undefined) {
            if ((selectedProduct.addons != null && selectedProduct.addons.length > 0 && !selectedProduct.product.enforceAddons) || (selectedProduct.variants != null && selectedProduct.variants.length > 0 && !selectedProduct.product.enforceVariants) ||
                (selectedProduct.addons != null && selectedProduct.addons.length > 0) || (selectedProduct.note != null && selectedProduct.note != '') || (selectedProduct.discount != null && selectedProduct.discount != 0)) {
                return true;
            }
            else return false;
        }

        if (selectedCombi != undefined) {
            if ((selectedCombi.discount != null && selectedCombi.discount != 0) || (selectedCombi.note != null && selectedCombi.note != '')) {
                return true
            }
            else return false;
        }
        return false;
    }

    useEffect(() => {
        if (totalHeight !== null) {
            const screenHeight = Dimensions.get('window').height;
            const discountOffset = activeOrder?.orderDiscountValue ? 160 : 120;
            const emptySpace = screenHeight - 257 - totalHeight - discountOffset;
            const calculatedEmptyRows = Math.max(Math.floor(emptySpace / 40), 0);
            setEmptyRowCount(calculatedEmptyRows);
        }
    }, [totalHeight, activeOrder]);

    const handleAddNoteForItem = () => {
        console.log(isAddNotePopupOpen);
        setIsAddNotePopupOpen(true);
    }



    const handleAddAddonForProduct = () => {
        if (selectedProductIds != null && selectedProductIds.length > 0) {
            const foundProduct = orderProducts.find(x => x.id == selectedProductIds[0]) || null;
            setAddingAddonForProduct(foundProduct);
        }
    };

    const handleAddVariantForProduct = () => {
        if (selectedProductIds != null && selectedProductIds.length > 0) {
            const foundProduct = orderProducts.find(x => x.id == selectedProductIds[0]) || null;
            console.log(foundProduct);

            setAddingVariantForProduct(foundProduct);
        }
    }

    const shouldDisplayAddAddon = () => {
        if (selectedProductIds != null && selectedProductIds.length > 0) {
            const foundProduct = orderProducts.find(x => x.id == selectedProductIds[0]) || null;
            const product = products.find(x => x.id == foundProduct?.productId);
            if (product?.addons != null && product?.addons.length > 0) {
                return true;
            }
        }

        return false;
    }

    const shouldDisplayAddVariant = () => {
        if (selectedProductIds != null && selectedProductIds.length > 0) {
            const foundProduct = orderProducts.find(x => x.id == selectedProductIds[0]) || null;
            const product = products.find(x => x.id == foundProduct?.productId);
            if (product?.variants != null && product?.variants.length > 0) {
                return true;
            }
        }

        return false;
    }

    const closeAddNoteForProduct = () => {
        setIsAddNotePopupOpen(false);
    }

    const closeAddAddonForProduct = () => {
        if (addingAddonForProduct != null) {
            setAddingAddonForProduct(null);
        }
    }

    const closeAddVariantForProduct = () => {
        if (addingVariantForProduct != null) {
            setAddingVariantForProduct(null);
        }
    }

    const handleSaveNote = async (note: string) => {
        if (activeOrder) {
            setShowSpinner(true);
            setIsAddNotePopupOpen(false);
            const updatedProducts = activeOrder.products.map((product) =>
                selectedProductIds.includes(product.id) ? { ...product, note: note } : product
            );

            const updatedCombinations = activeOrder.combinations.map((combination) =>
                selectedCombinationIds.includes(combination.id) ? { ...combination, note: note } : combination
            );

            setOrderProducts(updatedProducts);
            const updatedOrder = {
                ...activeOrder,
                products: updatedProducts,
                combinations: updatedCombinations,
            };
            setActiveOrder(updatedOrder);
            await saveOrder(updatedOrder);
            setShowSpinner(false);
        }
    };


    const handleSaveAddonsForProduct = (addons: OrderProductAddon[]) => {
        if (addingAddonForProduct != null) {
            setOrderProducts((prevOrderProducts) => {
                if (prevOrderProducts.length === 0) return prevOrderProducts;
                const prod = addingAddonForProduct;
                const updatedProduct = {
                    ...prod,
                    addons: addons,
                };

                const updatedProducts = prevOrderProducts.map((product) =>
                    product.id == addingAddonForProduct.id ? updatedProduct : product
                );

                if (activeOrder) {
                    const updatedOrder = {
                        ...activeOrder,
                        products: updatedProducts,
                    };
                    setActiveOrder(updatedOrder);
                    saveOrder(updatedOrder);
                }

                return prevOrderProducts.map((product) =>
                    product.id == addingAddonForProduct.id ? updatedProduct : product
                );
            });
        }
        setAddingAddonForProduct(null);
    }

    const handleSaveVariantForProduct = (variant: ProductVariant | null) => {
        if (addingVariantForProduct != null) {
            setOrderProducts((prevOrderProducts) => {
                if (prevOrderProducts.length === 0) return prevOrderProducts;
                const prod = addingVariantForProduct;
                const updatedProduct = {
                    ...prod,
                    variants: variant != null ? [variant] : [],
                };

                const updatedProducts = prevOrderProducts.map((product) =>
                    product.id == addingVariantForProduct.id ? updatedProduct : product
                );

                if (activeOrder) {
                    const updatedOrder = {
                        ...activeOrder,
                        products: updatedProducts,
                    };
                    setActiveOrder(updatedOrder);
                    saveOrder(updatedOrder);
                }

                return prevOrderProducts.map((product) =>
                    product.id == addingVariantForProduct.id ? updatedProduct : product
                );
            });
        }
        setAddingVariantForProduct(null);
    }

    const handleCancelLongPress = () => {
        if (longPressTimeout) {
            clearTimeout(longPressTimeout);
        }
    };

    const handleCategoryClick = (category: Category) => {
        setSelectedCategory(category);
        setIsCombinationsSelected(false);
        const filtered = products.filter((product) => product.categoryName === category.name && product.name.toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredProducts(filtered);
    };

    const openReceipt = () => {
        const freshResults = computeMealPlanResults();
        setSeatResults(freshResults);
        // Use fresh results to check included status (state hasn't updated yet)
        const isItemIncluded = (p: OrderProduct) => {
            if (p.seatId && freshResults[p.seatId]) {
                const ev = freshResults[p.seatId].evaluations?.find((e: any) => e.orderProductId === p.id);
                if (ev) return ev.status === 'included';
            }
            return (p as any).mealPlanStatus === 'included';
        };
        const sub = orderProducts.reduce((s, p) => {
            if (isItemIncluded(p)) return s;
            // Variant price replaces the base product price.
            const pv = p.variants?.find(v => v?.priceWithoutTax != null && v.priceWithoutTax !== 0);
            const unit = pv?.priceWithoutTax ?? p.price;
            return s + (unit * p.qty);
        }, 0);
        setCapturedTotal(sub);
        setIsReceiptPopupOpen(true);
    }

    const getProductPriceValue = (orderProduct: OrderProduct) => {

        // A selected variant's price REPLACES the base product price (not additive).
        const pricedVariant = orderProduct.variants?.find(v => v?.priceWithoutTax != null && v.priceWithoutTax !== 0);
        let totalPrice = pricedVariant?.priceWithoutTax ?? orderProduct.price;

        orderProduct.addons.forEach(addon => {
            if (addon.addon?.priceWithoutTax) {
                totalPrice = totalPrice + addon.addon?.priceWithoutTax * addon.qty;
            }
        });

        if (orderProduct.discount && orderProduct.discount > 0) {
            if (orderProduct.discountType == "amount") {
                totalPrice = totalPrice - orderProduct.discount;
            }
            else if (orderProduct.discountType == "percentage") {
                totalPrice = totalPrice - ((orderProduct.discount / 100) * totalPrice);
            }
        }

        return totalPrice;
    };


    const getCombinationPriceValue = (combination: OrderCombination) => {

        let totalPrice = combination.price;

        if (combination.discount && combination.discount > 0) {
            if (combination.discountType == "amount") {
                totalPrice = totalPrice - combination.discount;
            }
            else if (combination.discountType == "percentage") {
                totalPrice = totalPrice - ((combination.discount / 100) * totalPrice);
            }
        }

        return totalPrice;
    };

    function generateGUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    const addProductToOrder = (newOrderProduct: OrderProduct, prod: Product) => {
        if (!hasPerm('pos-app.order.create')) return;
        const hasVariants = !!(prod.variants && prod.variants.length > 0);
        const hasAddons = !!(prod.addons && prod.addons.length > 0);
        const seatForItem = newOrderProduct.seatId ? seats.find(s => s.id === newOrderProduct.seatId) : null;
        const seatHasMealPlan = !!(seatForItem && seatForItem.type === 'hotel_guest' && seatForItem.rateId && seatConfigs[seatForItem.rateId]);

        // For meal-plan seats we always store individually so the engine evaluates
        // each row in isolation. Display grouping is handled at render time.
        const canMerge = !hasVariants && !hasAddons
            && !seatHasMealPlan
            && !newOrderProduct.mealPlanOverride
            && !newOrderProduct.note
            && (!newOrderProduct.addons || newOrderProduct.addons.length === 0)
            && (!newOrderProduct.variants || newOrderProduct.variants.length === 0);

        let newProductList: OrderProduct[];
        if (canMerge) {
            const existingIdx = orderProducts.findIndex(p =>
                p.productId === newOrderProduct.productId
                && (p.seatId || undefined) === (newOrderProduct.seatId || undefined)
                && !p.sentToKitchen
                && !p.note
                && (!p.addons || p.addons.length === 0)
                && (!p.variants || p.variants.length === 0)
                && !p.mealPlanOverride
                && (p.discount || 0) === (newOrderProduct.discount || 0)
                && (p.discountType || '') === (newOrderProduct.discountType || '')
            );
            if (existingIdx >= 0) {
                newProductList = [...orderProducts];
                newProductList[existingIdx] = {
                    ...newProductList[existingIdx],
                    qty: (newProductList[existingIdx].qty || 1) + (newOrderProduct.qty || 1),
                };
            } else {
                newProductList = [...orderProducts, newOrderProduct];
            }
        } else {
            newProductList = [...orderProducts, newOrderProduct];
        }

        setOrderProducts(newProductList);
        setSelectedProductToAdd(prod);

        if (hasVariants) {
            setIsAddVariantOpen(true);
        }
        else if (hasAddons) {
            setIsAddAddonOpen(true);
        }
        else {
            setSelectedProductToAdd(null);
        }

        if (activeOrder) {
            const updatedOrder = { ...activeOrder, products: newProductList, date: new Date() };
            saveOrder(updatedOrder);
        }
    }

    const handleProductClick = (prod: Product) => {
        if (!hasPerm('pos-app.order.create')) return;
        // If seats exist but none selected, prompt
        if (seats.length > 0 && activeSeatId === null) {
            setErrorTitle('Select a Seat');
            setErrorContent('Please select a seat before adding items.');
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
            return;
        }

        // Get category name for meal plan mapping
        const category = categories.find(c => c.id === prod.categoryId);
        console.log('[MealPlan] Product clicked:', prod.name, '| categoryId:', prod.categoryId, '| categoryName from prod:', prod.categoryName, '| categoryName from lookup:', category?.name);

        // Check for customer discount matching this product's category
        let customerDiscount = 0;
        let customerDiscountType = '';
        const customerDiscounts = activeOrder?.customerDiscounts || [];
        if (customerDiscounts.length > 0) {
            const matchingDiscount = customerDiscounts.find(d =>
                d.applyToAll || (prod.categoryId && d.categoryIds.includes(prod.categoryId))
            );
            if (matchingDiscount) {
                customerDiscount = matchingDiscount.value;
                customerDiscountType = matchingDiscount.type === 'percentage' ? 'percentage' : 'amount';
            }
        }

        const newOrderProduct: OrderProduct = {
            id: generateGUID(),
            qty: 1,
            name: prod.name,
            product: prod,
            discount: customerDiscount,
            note: '',
            addons: [],
            variants: [],
            discountType: customerDiscountType,
            productId: prod.id,
            productName: prod.name,
            price: prod.price,
            sentToKitchen: false,
            taxRate: prod.taxRate,
            taxType: prod.taxType,
            categoryId: prod.categoryId,
            categoryName: category?.name,
            seatId: activeSeatId || undefined,
        };

        // Check if this product would be "extra" for a hotel guest with a meal plan
        if (activeSeatId && seats.length > 0) {
            const seat = seats.find(s => s.id === activeSeatId);
            if (seat && seat.type === 'hotel_guest' && seat.rateId) {
                const config = seatConfigs[seat.rateId] || null;
                if (config) {
                    // Evaluate with the new item included to determine its status
                    const currentSeatItems = orderProducts
                        .filter(p => p.seatId === seat.id)
                        .map(p => {
                            const cat = categories.find(c => c.id === p.categoryId);
                            return { ...p, categoryName: cat?.name || p.categoryName || '' };
                        });
                    // Evaluate current items (without the new one) to check if allowance is already full
                    const currentResult = mealPlanEngine.evaluateSeatItems(seat, currentSeatItems, config);
                    const mealCategory = category?.name ? getItemMealCategoryFromCategoryName(category.name, config) : 'other';

                    let wouldBeExtra = false;
                    let extraReason = '';

                    if (!currentResult.isWithinTimeWindow && currentResult.currentMealPeriod === null) {
                        // Outside meal period — everything is extra
                        wouldBeExtra = true;
                        extraReason = 'Outside meal period time window';
                    } else if (currentResult.isAllowanceAlreadyUsed) {
                        wouldBeExtra = true;
                        extraReason = 'Allowance already used this meal period';
                    } else if (mealCategory === 'drink' && !config.drinksIncluded) {
                        wouldBeExtra = true;
                        extraReason = 'Drinks not included in meal plan';
                    } else if (mealCategory === 'other') {
                        wouldBeExtra = true;
                        extraReason = 'Item category not covered by meal plan';
                    } else {
                        // Check if allowance for this category is already full
                        const summary = currentResult.allowanceSummary.find(s => s.category === mealCategory);
                        if (summary && summary.used >= summary.max) {
                            wouldBeExtra = true;
                            extraReason = `Exceeds meal plan allowance (${summary.max} ${summary.label} allowed)`;
                        }
                    }

                    console.log('[MealPlan Override] category:', mealCategory, 'wouldBeExtra:', wouldBeExtra, 'reason:', extraReason, 'summary:', currentResult.allowanceSummary);

                    if (wouldBeExtra) {
                        // Show override popup
                        setPendingOverrideProduct({
                            product: newOrderProduct,
                            extraReason,
                            guestName: seat.guestName,
                            roomNumber: seat.roomNumber,
                            sourceProduct: prod,
                        });
                        return;
                    }
                }
            }
        }

        addProductToOrder(newOrderProduct, prod);
    }

    const handleMealPlanOverrideCharge = () => {
        if (!pendingOverrideProduct) return;
        const { product, sourceProduct } = pendingOverrideProduct;
        setPendingOverrideProduct(null);
        addProductToOrder(product, sourceProduct!);
    };

    const handleMealPlanOverrideWaive = (reason: string) => {
        if (!pendingOverrideProduct) return;
        const { product, sourceProduct } = pendingOverrideProduct;
        const waived: OrderProduct = { ...product, mealPlanOverride: { waived: true, reason } };
        setPendingOverrideProduct(null);
        addProductToOrder(waived, sourceProduct!);
    };

    const handleMealPlanOverrideCancel = () => {
        setPendingOverrideProduct(null);
    };

    const handleSaveFlexibleCombinationOnOrder = async (orderComb: OrderCombination) => {
        setAddingFlexibleCombination(null);
        if (activeOrder) {
            const updatedOrder = {
                ...activeOrder,
                combinations: [...(activeOrder.combinations || []), orderComb],
                date: new Date()
            };
            setActiveOrder(updatedOrder);
            await saveOrder(updatedOrder);
        } else {
            console.error("activeOrder is undefined.");
        }
    }

    const handleUpdatingFlexibleCombinationOnOrder = async (orderComb: OrderCombination) => {
        if (activeOrder) {
            const updatedOrder = {
                ...activeOrder,
                combinations: activeOrder.combinations.map((combination) =>
                    combination.id === orderComb.id ? orderComb : combination
                ),
            };

            setIsEditingProductsForCombination(null);
            try {
                setActiveOrder(updatedOrder);
                await saveOrder(updatedOrder);
            } catch (error) {
                console.error("Error saving order:", error);
            }
        }
    };

    const handleCombinationClick = async (comb: Combination) => {
        if (comb.type === 0) {
            const newOrderCombination: OrderCombination = {
                id: generateGUID(),
                combinationId: comb.id || '',
                combinationPlu: comb.combinationId || '',
                combinationName: comb.name,
                type: comb.type,
                price: comb.price,
                selectedsubproductsNames: comb.subproducts
                    ? comb.subproducts
                        .map(subproduct => {
                            if (subproduct.productName) return subproduct.productName;
                            const product = products.find(p => p.id === subproduct.productId);
                            return product?.name ?? null;
                        })
                        .filter((name): name is string => name !== null && name !== undefined)
                    : [],
                selectedsubproductsIds: comb.subproducts ? comb.subproducts.map(subproduct => subproduct.id) : [],
                note: '',
                discount: 0,
                discountType: 'amount',
                sentToKitchen: false,
                taxRate: comb.taxRate,
                taxType: comb.taxType
            };

            if (activeOrder) {
                const updatedOrder = {
                    ...activeOrder,
                    combinations: [...(activeOrder.combinations || []), newOrderCombination],
                    date: new Date()
                };
                setActiveOrder(updatedOrder);
                await saveOrder(updatedOrder);
            } else {
                console.error("activeOrder is undefined.");
            }
        } else {
            setAddingFlexibleCombination(comb);
        }
    }

    // Record meal consumption for hotel-guest seats when their bill is settled. Defaults to every
    // seat on the order; a split room charge settles one seat at a time and passes just that one.
    const recordMealConsumptionsForOrder = useCallback(async (order: Order, onlySeats?: Seat[]) => {
        for (const seat of (onlySeats || seats)) {
            if (seat.type !== 'hotel_guest' || !seat.rateId) continue;
            const result = seatResults[seat.id];
            if (!result || !result.currentMealPeriod || result.totalIncluded === 0) continue;

            // Use the meal period captured at order creation, not the current time
            const mealPeriod = seat.capturedMealPeriod || result.currentMealPeriod;

            const includedItems = result.evaluations
                .filter(e => e.status === 'included')
                .map(e => {
                    const prod = orderProducts.find(p => p.id === e.orderProductId);
                    return {
                        orderProductId: e.orderProductId,
                        category: prod?.categoryName || '',
                        name: prod?.name || '',
                        price: prod?.price || 0,
                        isIncludedInPlan: true,
                    };
                });

            if (includedItems.length > 0) {
                await mealPlanEngine.recordConsumption(
                    seat,
                    mealPeriod,
                    includedItems,
                    order.id,
                );
            }
        }
    }, [seats, seatResults, orderProducts, mealPlanEngine]);

    const autoPrintReceipt = async (orderToPrint?: Order, skipSignature = false) => {
        const order = orderToPrint || activeOrder;
        if (!order) return;
        try {
            // Get receipt printer from floor
            let printer: any = null;
            if (floor?.receiptPrinterId) {
                const printerRes = await fetch(`${process.env.REACT_APP_API_URL}/Printer/${floor.receiptPrinterId}`, {
                    method: 'GET', credentials: 'include',
                });
                if (printerRes.ok) printer = await printerRes.json();
            }
            if (!printer) {
                console.warn('[AutoPrint] No receipt printer configured for this floor');
                return;
            }

            const formatDate = (date: Date): string => {
                return new Date(date).toLocaleString('en-US', {
                    weekday: 'short', month: 'long', day: '2-digit', year: 'numeric',
                    hour: 'numeric', minute: '2-digit', second: '2-digit',
                });
            };

            // Stamp products with meal plan status
            const stampedProducts = (order.products || []).map((p: any) => {
                if (p.seatId && seatResults[p.seatId]) {
                    const ev = seatResults[p.seatId].evaluations?.find((e: any) => e.orderProductId === p.id);
                    if (ev) return { ...p, mealPlanStatus: ev.status };
                }
                return p;
            });
            const stampedOrder = {
                ...order,
                products: stampedProducts,
                subtotal: getSubtotalFromUpdatedOrder({ ...order, products: stampedProducts } as Order),
                total: getTotalFromUpdatedOrder({ ...order, products: stampedProducts } as Order),
                tax: getTaxFromUpdatedOrder({ ...order, products: stampedProducts } as Order),
            };

            // Generate receipt via API
            let receiptHtmlUrl = '';
            let localHtml = '';
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/generate-order`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ order: stampedOrder, formattedDate: formatDate(new Date()), skipSignature }),
                    credentials: 'include',
                });
                if (response.ok) {
                    receiptHtmlUrl = await response.text();
                } else {
                    throw new Error('API failed');
                }
            } catch {
                // Offline fallback
                const { default: ReceiptService } = await import('../services/ReceiptService');
                localHtml = await ReceiptService.generateReceiptHtml(stampedOrder, formatDate(new Date()));
            }

            // Print via Electron or Capacitor
            if ((window as any).electronAPI?.printThermalReceipt) {
                if (receiptHtmlUrl) {
                    await window.electronAPI.printThermalReceipt(receiptHtmlUrl, { ...stampedOrder, printer: resolvePrinterForElectron(printer) });
                } else if (localHtml) {
                    const blob = new Blob([localHtml], { type: 'text/html' });
                    const blobUrl = URL.createObjectURL(blob);
                    await window.electronAPI.printThermalReceipt(blobUrl, { ...stampedOrder, printer: resolvePrinterForElectron(printer) });
                    URL.revokeObjectURL(blobUrl);
                }
            } else {
                try {
                    const { Capacitor } = await import('@capacitor/core');
                    if (Capacitor.isNativePlatform()) {
                        const { ThermalPrinter } = (Capacitor as any).Plugins;
                        if (ThermalPrinter) {
                            await ThermalPrinter.printHtml({
                                htmlUrl: receiptHtmlUrl || undefined,
                                htmlContent: localHtml || undefined,
                                ...nativePrintTarget(printer),
                                port: printer.port || 9100,
                                ticketWidthMm: printer.ticketWidth || 80,
                                autoCutter: printer.hasAutoCutter ?? true,
                                printerType: printer.type === 'Star Micronics' ? 'STAR' : 'EPSON',
                            });
                        }
                    }
                } catch (e) {
                    console.error('[AutoPrint] Native print failed:', e);
                }
            }
        } catch (error) {
            console.error('[AutoPrint] Failed to print receipt:', error);
        }
    };

    const handlePrintReceipt = async () => {
        if (orderHasNoProducts() || isPrinting) return;
        setIsPrinting(true);
        try {
            await autoPrintReceipt(undefined, true);
            setSuccessTitle('Print Sent');
            setSuccessContent('Receipt was sent to the printer.');
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch {
            setErrorTitle('Print Failed');
            setErrorContent('Failed to print receipt. Please try again.');
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
        } finally {
            setIsPrinting(false);
        }
    };

    const generateSplitReceiptBlob = async (seatId: string) => {
        if (seats.length <= 1 || !activeOrder) return;
        const seat = seats.find(s => s.id === seatId);
        if (!seat) return;
        const seatIndex = seats.indexOf(seat) + 1;
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/Order/generate-split-receipt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    order: { ...activeOrder, products: orderProducts.map(p => { let ms = 'none'; if (p.seatId && seatResults[p.seatId]) { const ev = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id); if (ev) ms = ev.status; } return { ...p, mealPlanStatus: ms }; }) },
                    seatId: seat.id,
                    splitIndex: seatIndex,
                    guestName: seat.guestName || '',
                    roomNumber: seat.roomNumber || '',
                    formattedDate: new Date().toLocaleString('en-US', { weekday: 'short', month: 'long', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }),
                }),
                credentials: 'include',
            });
        } catch (e) {
            console.error('[SplitReceipt] Failed to pre-generate split receipt blob:', e);
        }
    };

    // Serialize all order saves so rapid successive edits (e.g. deleting items
    // one-by-one) are persisted in call order. Without this, each saveOrder does a
    // GET then a POST independently, so POSTs can land out of order and an earlier
    // save (still containing an item) can overwrite a later one (the emptied order),
    // making the last deleted item reappear.
    const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

    const saveOrder = (updatedOrder: Order): Promise<void> => {
        const next = saveQueueRef.current
            .catch(() => {})
            .then(() => saveOrderInternal(updatedOrder));
        saveQueueRef.current = next.catch(() => {});
        return next;
    };

    const saveOrderInternal = async (updatedOrder: Order) => {
        // Check if order was closed by another user before saving
        try {
            const checkRes = await fetch(`${process.env.REACT_APP_API_URL}/Order/${updatedOrder.id}`, {
                method: 'GET',
                credentials: 'include',
            });
            if (checkRes.ok) {
                const latestOrder = await checkRes.json();
                if (latestOrder && !latestOrder.isOpen) {
                    const closedAt = latestOrder.orderPaidOn
                        ? new Date(latestOrder.orderPaidOn).toLocaleString('en-US', {
                            weekday: 'short', month: 'long', day: '2-digit', year: 'numeric',
                            hour: 'numeric', minute: '2-digit', second: '2-digit',
                        })
                        : 'unknown time';
                    setOrderClosedInfo({
                        closedBy: latestOrder.closedBy || 'another user',
                        closedAt,
                    });
                    // Log this event
                    try {
                        const closedAtLog = latestOrder.orderPaidOn
                            ? new Date(latestOrder.orderPaidOn).toLocaleString('en-GB', {
                                hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric',
                            })
                            : 'unknown time';
                        await fetch(`${process.env.REACT_APP_API_URL}/UserActivityLog/log`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                                entityType: 'Order',
                                entityId: updatedOrder.id,
                                entityNumber: updatedOrder.orderNumber,
                                activityType: 'Updated',
                                description: `Tried to update order #${updatedOrder.orderNumber || 'N/A'} but order was already closed by ${latestOrder.closedBy || 'unknown'} at ${closedAtLog}`,
                                details: JSON.stringify({
                                    closedBy: latestOrder.closedBy,
                                    closedAt: latestOrder.orderPaidOn,
                                    orderTotal: latestOrder.total,
                                    tableName: latestOrder.tableName,
                                    tableId: latestOrder.tableId,
                                }),
                                source: 'POS',
                                isAdvanced: true,
                            }),
                        });
                    } catch { }
                    return;
                }
            }
        } catch {
            // If check fails (e.g. offline), proceed with save
        }

        // Stamp mealPlanStatus from live seatResults before persisting
        const stampedProducts = (updatedOrder.products || []).map((p: any) => {
            if (p.seatId && seatResults[p.seatId]) {
                const ev = seatResults[p.seatId].evaluations?.find((e: any) => e.orderProductId === p.id);
                if (ev) return { ...p, mealPlanStatus: ev.status };
            }
            return p;
        });

        const orderPayload = {
            id: updatedOrder.id,
            propertyId: updatedOrder.propertyId,
            products: stampedProducts,
            customerEmail: updatedOrder.customerEmail,
            customerName: updatedOrder.customerName,
            customerId: updatedOrder.customerId || activeOrder?.customerId,
            currency: updatedOrder.currency,
            subtotal: getSubtotalFromUpdatedOrder(updatedOrder),
            total: getTotalFromUpdatedOrder(updatedOrder),
            tax: getTaxFromUpdatedOrder(updatedOrder),
            isTaxPercentage: updatedOrder.isTaxPercentage,
            discount: getDiscount(),
            isOpen: updatedOrder.isOpen,
            tableId: updatedOrder.tableId,
            tableName: updatedOrder.tableName,
            orderDiscountType: updatedOrder.orderDiscountType,
            orderDiscountValue: updatedOrder.orderDiscountValue,
            orderDiscountName: updatedOrder.orderDiscountName,
            status: updatedOrder.status,
            orderNumber: updatedOrder.orderNumber,
            waiter: updatedOrder.waiter,
            closedBy: updatedOrder.closedBy,
            createdBy: updatedOrder.createdBy,
            orderPaidOn: updatedOrder.orderPaidOn,
            isVoided: updatedOrder.isVoided,
            combinations: updatedOrder.combinations,
            subtotalWithoutDiscounts: updatedOrder.subtotalWithoutDiscounts,
            payments: updatedOrder.payments,
            mergedTablesIds: updatedOrder.mergedTablesIds,
            mergedTablesNames: updatedOrder.mergedTablesNames,
            roomNumber: updatedOrder.roomNumber || activeOrder?.roomNumber || linkedRoomNumber,
            prostayReservationNumber: updatedOrder.prostayReservationNumber || activeOrder?.prostayReservationNumber || linkedReservationNumber,
            orderRoomAllocations: updatedOrder.orderRoomAllocations || activeOrder?.orderRoomAllocations || [],
            seats: updatedOrder.seats || seats,
            date: updatedOrder.date,
        };

        // Always save locally for offline access
        putOrderToDb(orderPayload).catch(() => {});

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(orderPayload),
                credentials: 'include',
            });

            if (response.status === 409) {
                const conflict = await response.json();
                const closedAt = conflict.orderPaidOn
                    ? new Date(conflict.orderPaidOn).toLocaleString('en-US', {
                        weekday: 'short', month: 'long', day: '2-digit', year: 'numeric',
                        hour: 'numeric', minute: '2-digit', second: '2-digit',
                    })
                    : 'unknown time';
                setOrderClosedInfo({
                    closedBy: conflict.closedBy || 'another user',
                    closedAt,
                });
                return;
            }

            // Record meal consumption when order is closed/paid
            if (!updatedOrder.isOpen && seats.length > 0) {
                recordMealConsumptionsForOrder(updatedOrder);
                localStorage.removeItem(`seats_${tableId}`);
            }
        } catch (error) {
            console.warn('[Offline] Save failed, queuing for sync', error);
            // Queue for sync when back online
            const { enqueueMutation } = await import('../services/OfflineDb');
            await enqueueMutation({
                type: 'ORDER_UPDATE',
                url: `${process.env.REACT_APP_API_URL}/Order/update`,
                method: 'POST',
                body: JSON.stringify(orderPayload),
            });

            if (!updatedOrder.isOpen && seats.length > 0) {
                recordMealConsumptionsForOrder(updatedOrder);
                localStorage.removeItem(`seats_${tableId}`);
            }
        }
    };

    useEffect(() => {
        if (selectedCategory && products.length > 0) {
            setFilteredProducts(
                products.filter(p => p.categoryName === selectedCategory.name)
            );
        }
    }, [selectedCategory, products]);

    const fetchOrder = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/get-active-order-for-table/` + tableId, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();

            const populatedOrderProducts = data.products.map((orderProduct: OrderProduct) => {
                const product = products.find((p: Product) => p.id === orderProduct.productId);

                const populatedAddons = orderProduct.addons.map((orderAddon: OrderProductAddon) => {
                    const addonDetails = product?.addons.find((prodAddon: ProductAddon) => prodAddon.id === orderAddon.addonId);
                    return {
                        ...orderAddon,
                        addon: addonDetails,
                    };
                });

                // Preserve seatId and categoryName from local state (API doesn't store these yet)
                const existingLocal = orderProducts.find(p => p.id === orderProduct.id);

                return {
                    ...orderProduct,
                    product: product || null,
                    addons: populatedAddons,
                    seatId: existingLocal?.seatId || orderProduct.seatId,
                    categoryName: existingLocal?.categoryName || product?.categoryName || orderProduct.categoryName,
                };
            });

            setActiveOrder(prev => ({
                ...data,
                products: populatedOrderProducts,
                seats: seats.length > 0 ? seats : data.seats,
                prostayReservationNumber: data.prostayReservationNumber || prev?.prostayReservationNumber,
                roomNumber: data.roomNumber || prev?.roomNumber,
                customerName: data.customerName || prev?.customerName,
                // Falls back to what we already hold so a refetch cannot blank the link
                // while the API does not yet echo the field back.
                customerId: data.customerId || prev?.customerId || (seats.length > 0 ? seats : data.seats)?.find((s: Seat) => s.customerId)?.customerId,
            }));

            setOrderProducts(populatedOrderProducts);
        } catch (error) {
            console.warn("Fetch order failed, trying offline cache: ", error);
            // Try to load from IndexedDB
            try {
                const { getActiveOrderByTableId } = await import('../services/OfflineDb');
                const cached = await getActiveOrderByTableId(tableId || '');
                if (cached) {
                    setActiveOrder(cached);
                    setOrderProducts(cached.products || []);
                }
            } catch (e) {
                console.error("Offline cache also failed: ", e);
            }
        }
    };

    const saveOrderWithNewTable = async (updatedOrder: Order) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/update-table`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: updatedOrder.id,
                    propertyId: updatedOrder.propertyId,
                    products: updatedOrder.products,
                    customerEmail: updatedOrder.customerEmail,
                    customerName: updatedOrder.customerName,
                    customerId: updatedOrder.customerId || activeOrder?.customerId,
                    currency: updatedOrder.currency,
                    subtotal: getSubtotalFromUpdatedOrder(updatedOrder),
                    total: getTotalFromUpdatedOrder(updatedOrder),
                    tax: getTaxFromUpdatedOrder(updatedOrder),
                    isTaxPercentage: updatedOrder.isTaxPercentage,
                    discount: getDiscount(),
                    isOpen: updatedOrder.isOpen,
                    tableId: updatedOrder.tableId,
                    tableName: updatedOrder.tableName,
                    orderDiscountType: updatedOrder.orderDiscountType,
                    orderDiscountValue: updatedOrder.orderDiscountValue,
                    orderDiscountName: updatedOrder.orderDiscountName,
                    status: updatedOrder.status,
                    orderNumber: updatedOrder.orderNumber,
                    waiter: updatedOrder.waiter,
                    date: updatedOrder.date,
                    closedBy: updatedOrder.closedBy,
                    createdBy: updatedOrder.createdBy,
                    orderPaidOn: updatedOrder.orderPaidOn,
                    isVoided: updatedOrder.isVoided,
                    combinations: updatedOrder.combinations,
                    subtotalWithoutDiscounts: updatedOrder.subtotalWithoutDiscounts,
                    payments: updatedOrder.payments
                }),
                credentials: 'include',
            });
            if (response) {

            }

        } catch (error) {
            console.error(error);
        }
    };

    const handleOnNextInVariant = () => {
        if (selectedProductToAdd?.addons && selectedProductToAdd?.addons.length > 0) {
            setIsAddVariantOpen(false);
            setIsAddAddonOpen(true);
        }
        else {
            setSelectedProductToAdd(null);
            setIsAddVariantOpen(false);
        }
    }

    const handleSaveAddons = (selectedAddons: OrderProductAddon[]) => {
        setOrderProducts((prevOrderProducts) => {
            if (prevOrderProducts.length === 0) return prevOrderProducts;

            const lastProduct = prevOrderProducts[prevOrderProducts.length - 1];
            const updatedProduct = { ...lastProduct, addons: selectedAddons };
            const updatedProducts = [
                ...prevOrderProducts.slice(0, prevOrderProducts.length - 1),
                updatedProduct,
            ];

            if (activeOrder) {
                // 1. Construct a new, updated order object
                const newOrder = {
                    ...activeOrder,
                    products: updatedProducts
                };

                // 2. Use *updatedProducts*, not the old state
                const newSubtotal = getSubtotalFromUpdatedOrder(newOrder);
                const newTax = getTaxFromUpdatedOrder(newOrder);
                const newTotal = getTotalFromUpdatedOrder(newOrder);

                // 3. Save those computed values into the new order
                const finalOrder = {
                    ...newOrder,
                    subtotal: newSubtotal,
                    tax: newTax,
                    total: newTotal,
                };

                // 4. Set state & save
                setActiveOrder(finalOrder);
                saveOrder(finalOrder);
            }

            return updatedProducts;
        });

        setSelectedProductToAdd(null);
        setIsAddAddonOpen(false);
    };

    const handleVariantSelected = (variant: ProductVariant) => {
        setOrderProducts((prevOrderProducts) => {
            if (prevOrderProducts.length === 0) return prevOrderProducts;

            const lastProduct = prevOrderProducts[prevOrderProducts.length - 1];

            const updatedProduct = {
                ...lastProduct,
                variants: [...(lastProduct.variants || []), variant],
            };

            const updatedProducts = [
                ...prevOrderProducts.slice(0, prevOrderProducts.length - 1),
                updatedProduct,
            ]

            if (activeOrder) {
                const updatedOrder = {
                    ...activeOrder,
                    products: updatedProducts,
                    subtotal: getSubtotal(),
                    total: getTotal(),
                    tax: getTax(),
                };
                setActiveOrder(updatedOrder);
                saveOrder(updatedOrder);
            }

            return [
                ...prevOrderProducts.slice(0, prevOrderProducts.length - 1),
                updatedProduct,
            ];
        });

        if (selectedProductToAdd?.addons && selectedProductToAdd?.addons.length > 0) {
            setIsAddVariantOpen(false);
            setIsAddAddonOpen(true);
        }
        else {
            setSelectedProductToAdd(null);
            setIsAddVariantOpen(false);
        }
    }

    useEffect(() => {
        // Resolves false when neither the network nor the cache produced any products, so the
        // grid can show a retry instead of an unexplained blank screen.
        const fetchProducts = async (): Promise<boolean> => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Product`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Network response was not ok');
                const data = await response.json();
                setProducts(data);
                setFilteredProducts(data);
                setFavorites(data.filter((product: { isFavorite: boolean; }) => product.isFavorite));
                // Cache for offline
                putRef('products', data).catch(() => {});
                return true;
            } catch (error) {
                console.warn("Fetch products failed, trying cache: ", error);
                const cached = await getRef('products');
                if (cached && cached.length > 0) {
                    setProducts(cached);
                    setFilteredProducts(cached);
                    setFavorites(cached.filter((p: any) => p.isFavorite));
                    return true;
                }
                return false;
            }
        };

        const fetchCategories = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/ProductCategory`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Network response was not ok');
                const data = await response.json();
                setCategories(data);
                setSelectedCategory(data.find((cat: Category) => cat.isActive));
                putRef('categories', data).catch(() => {});
            } catch (error) {
                console.warn("Fetch categories failed, trying cache: ", error);
                const cached = await getRef('categories');
                if (cached) {
                    setCategories(cached);
                    setSelectedCategory(cached.find((cat: Category) => cat.isActive));
                }
            }
        };

        const fetchCombinations = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Combination/get-all-active`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Network response was not ok');
                const data = await response.json();
                setCombinations(data);
                putRef('combinations', data).catch(() => {});
            } catch (error) {
                console.warn("Fetch combinations failed, trying cache: ", error);
                const cached = await getRef('combinations');
                if (cached) setCombinations(cached);
            }
        };

        const fetchData = async () => {
            setCatalogLoading(true);
            setCatalogFailed(false);
            try {
                // In parallel: none of the three depends on the others, and run back to back they
                // cost three sequential round trips before the grid can paint anything.
                const [gotProducts] = await Promise.all([
                    fetchProducts(),
                    fetchCategories(),
                    fetchCombinations(),
                ]);
                setCatalogFailed(!gotProducts);
                // With no products the order effect never runs, so release its flag here —
                // otherwise the page would sit behind the spinner forever.
                if (!gotProducts) setOrderLoading(false);
            } catch (error) {
                console.warn('Catalog load failed: ', error);
                setCatalogFailed(true);
                setOrderLoading(false);
            } finally {
                // finally, so a throw can never strand the page behind a permanent spinner
                setCatalogLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogReloadKey]);

    useEffect(() => {
        const fetchData = async () => {
            if (selectedFloorId) {
                setShowSpinner(true);
                await fetchFloor(selectedFloorId);
                await fetchAvailableEmptyTablesForFloor(selectedFloorId);
                setShowSpinner(false);
            }
        };
        fetchData();
    }, [selectedFloorId]);

    useEffect(() => {
        // The order can only be populated once the catalog is in. `orderLoading` starts true and
        // stays true across this early return, so the page keeps showing the spinner instead of
        // flashing an empty ticket. The catalog effect clears it if products never arrive.
        if (products.length === 0) return;

        const fetchOrder = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/get-active-order-for-table/` + tableId, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();

                const populatedOrderProducts = data.products.map((orderProduct: OrderProduct) => {
                    const product = products.find((p: Product) => p.id === orderProduct.productId);

                    const populatedAddons = orderProduct.addons.map((orderAddon: OrderProductAddon) => {
                        const addonDetails = product?.addons.find((prodAddon: ProductAddon) => prodAddon.id === orderAddon.addonId);
                        return {
                            ...orderAddon,
                            addon: addonDetails,
                        };
                    });

                    return {
                        ...orderProduct,
                        product: product || null,
                        addons: populatedAddons
                    };
                });

                setActiveOrder({
                    ...data,
                    products: populatedOrderProducts,
                });

                setOrderProducts(populatedOrderProducts);

                // Restore seats from the order immediately so they're available before the setup dialog check
                if (data.seats && data.seats.length > 0) {
                    setSeats(data.seats);
                } else if (!data.seats || data.seats.length === 0) {
                    // The active order for this table has no seats — i.e. it's a fresh order (the
                    // previous one was closed/paid). Clear any stale seats restored from localStorage
                    // so the setup dialog opens blank instead of pre-filling the previous guest's name.
                    setSeats([]);
                    setActiveSeatId(null);
                    localStorage.removeItem(`seats_${tableId}`);
                    setShowTableSetup(true);
                }

                console.log('[DEBUG] Active order loaded:', data.id, 'products:', populatedOrderProducts.length, 'seats:', data.seats?.length || 0);
            } catch (error) {
                console.log('[DEBUG] No active order for this table (this is normal for empty tables)');
                setShowTableSetup(true);
            }
            setTableSetupChecked(true);
        };

        const fetchData = async () => {
            setOrderLoading(true);
            try {
                await fetchOrder();
            } finally {
                setOrderLoading(false);
            }
        };

        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableId, products]);

    // Table setup dialog is now triggered directly in fetchOrder (only when no order exists for the table)

    // Restore seats from active order if they exist
    useEffect(() => {
        if (activeOrder?.seats && activeOrder.seats.length > 0 && seats.length === 0) {
            setSeats(activeOrder.seats);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeOrder?.seats]);

    const applyCustomerDiscountsToProducts = (products: OrderProduct[], allDiscounts: CustomerDiscount[]): OrderProduct[] => {
        if (!allDiscounts || allDiscounts.length === 0) return products;
        return products.map(product => {
            // Skip products that already have a manual discount
            if (product.discount && product.discount > 0) return product;
            const matchingDiscount = allDiscounts.find(d =>
                d.applyToAll || (product.categoryId && d.categoryIds.includes(product.categoryId))
            );
            if (matchingDiscount) {
                return {
                    ...product,
                    discount: matchingDiscount.value,
                    discountType: matchingDiscount.type === 'percentage' ? 'percentage' : 'amount',
                };
            }
            return product;
        });
    };

    const mergeCustomerDiscounts = (customers: Customer[]): CustomerDiscount[] => {
        const merged: CustomerDiscount[] = [];
        const seenIds = new Set<string>();
        for (const c of customers) {
            for (const d of (c.discounts || [])) {
                if (d.id && seenIds.has(d.id)) continue;
                if (d.id) seenIds.add(d.id);
                merged.push(d);
            }
        }
        return merged;
    };

    const handleTableSetupConfirm = (newSeats: Seat[], walkInCustomers: Customer[] = []) => {
        // Close the loop on a floor-plan reservation: the party is now seated on this table
        // with this order, so the booking should stop showing as expected.
        if (seatingHandoff && tableId) {
            const handoff = seatingHandoff;
            setSeatingHandoff(null);
            clearSeatingHandoff();
            ReservationService.seat(handoff.reservationId, activeOrder?.id, [tableId])
                .catch((e) => console.warn('[TableOrder] could not mark reservation seated', e));
        }

        // Capture current meal period per seat so time shifts don't affect recording
        const capturedDate = localDateString();
        const seatsWithPeriod = newSeats.map(s => {
            if (s.type === 'hotel_guest' && s.rateId) {
                const config = seatConfigs[s.rateId] || null;
                const period = mealPlanEngine.getCurrentMealPeriod(config);
                // The date travels with the seat: the API records the allowance when the order
                // closes, and its clock is not necessarily the property's.
                return { ...s, capturedMealPeriod: period || undefined, capturedDate };
            }
            return s;
        });
        setSeats(seatsWithPeriod);
        setShowTableSetup(false);
        if (newSeats.length > 0) {
            setActiveSeatId(newSeats[0].id);
        }
        // Link reservation from first hotel guest to order and save immediately
        if (activeOrder) {
            const firstHotelGuest = newSeats.find(s => s.type === 'hotel_guest' && s.reservationNumber);
            const hasHotelGuest = newSeats.some(s => s.type === 'hotel_guest');
            const resNum = firstHotelGuest?.reservationNumber || (hasHotelGuest ? (activeOrder.prostayReservationNumber || '') : '');
            const roomNum = firstHotelGuest?.roomNumber || (hasHotelGuest ? (activeOrder.roomNumber || '') : '');
            setLinkedReservationNumber(resNum);
            setLinkedRoomNumber(roomNum);
            const orderRoomAllocations = newSeats
                .filter(s => s.type === 'hotel_guest' && s.reservationId && s.roomNumber && s.roomWithStatusId)
                .map(s => ({
                    reservationId: s.reservationId!,
                    roomNumber: s.roomNumber!,
                    roomWithStatusId: s.roomWithStatusId!,
                }));
            // Merge discounts from walk-in customers and apply them to the order's products.
            const mergedDiscounts = mergeCustomerDiscounts(walkInCustomers);
            const updatedProducts = applyCustomerDiscountsToProducts(activeOrder.products || [], mergedDiscounts);

            // Carry the customer's id, not just their name. Walk-ins created here have no
            // email, so without this the order has nothing that resolves to their record.
            const primaryCustomerId = newSeats.find(s => s.customerId)?.customerId;

            const updatedOrder = {
                ...activeOrder,
                // The stamped seats, not the raw ones: saving newSeats dropped the captured meal
                // period and date on the way to the database, leaving the API with nothing to
                // identify which allowance the seat spent when the order was closed.
                seats: seatsWithPeriod,
                prostayReservationNumber: resNum,
                roomNumber: roomNum,
                customerName: newSeats.filter(s => s.guestName).map(s => s.guestName).join(', ') || activeOrder.customerName,
                customerId: primaryCustomerId || activeOrder.customerId,
                orderRoomAllocations,
                products: updatedProducts,
                customerDiscounts: mergedDiscounts.length > 0 ? mergedDiscounts : activeOrder.customerDiscounts,
            };
            setActiveOrder(updatedOrder);
            saveOrder(updatedOrder);
        }
    };

    const handleTableSetupCancel = () => {
        // Backing out means the party was not seated - drop the parked intent so it cannot
        // attach itself to a later, unrelated setup on this table.
        if (seatingHandoff) {
            setSeatingHandoff(null);
            clearSeatingHandoff();
        }
        setShowTableSetup(false);
        setTableSetupChecked(false); // prevent re-opening
    };

    const handleAddSeat = () => {
        const newSeat: Seat = {
            id: `seat-${Date.now()}`,
            seatNumber: seats.length + 1,
            type: 'walk_in',
            guestName: `Guest ${seats.length + 1}`,
        };
        const newSeats = [...seats, newSeat];
        setSeats(newSeats);
        setActiveSeatId(newSeat.id);
        if (activeOrder) {
            const updatedOrder = { ...activeOrder, seats: newSeats };
            setActiveOrder(updatedOrder);
            saveOrder(updatedOrder);
        }
    };

    const handleRemoveSeat = (seatId: string) => {
        const seat = seats.find(s => s.id === seatId);
        if (!seat) return;
        const itemCount = orderProducts.filter(p => p.seatId === seatId).length
            + (activeOrder?.combinations || []).filter(c => c.seatId === seatId).length;
        if (itemCount > 0) {
            const ok = window.confirm(
                `Remove ${seat.guestName || `Guest ${seat.seatNumber}`}? Their ${itemCount} item${itemCount > 1 ? 's' : ''} will move to Unassigned (they stay on the check).`,
            );
            if (!ok) return;
        }
        const newSeats = seats.filter(s => s.id !== seatId);
        // Items belonging to the removed seat fall back to unassigned rather than being deleted.
        const updatedProducts = orderProducts.map(p => (p.seatId === seatId ? { ...p, seatId: undefined } : p));
        const updatedCombinations = (activeOrder?.combinations || []).map(c => (c.seatId === seatId ? { ...c, seatId: undefined } : c));
        setSeats(newSeats);
        setOrderProducts(updatedProducts);
        if (activeSeatId === seatId) setActiveSeatId(null);
        if (activeOrder) {
            const updatedOrder = { ...activeOrder, seats: newSeats, products: updatedProducts, combinations: updatedCombinations };
            setActiveOrder(updatedOrder);
            saveOrder(updatedOrder);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        const filtered = products.filter(product =>
            product.name.toLowerCase().includes(value.toLowerCase()) && product.categoryName === selectedCategory?.name
        );
        setFilteredProducts(filtered);
    };

    const handleAddDiscount = () => {
        setIsAddDiscountPopupOpen(true);
        setIsTableActionsPopupOpen(false);
    }

    const handleChangeTables = () => {
        setIsChangeTablePopupOpen(true);
        setIsTableActionsPopupOpen(false);
    }

    const handleMergeTables = () => {
        localStorage.setItem('tableToMergeFrom', JSON.stringify(activeOrder?.tableId));
        localStorage.setItem('orderToMerge', JSON.stringify(activeOrder));
        // Ensure current seats are saved before navigating
        if (seats.length > 0) {
            localStorage.setItem(`seats_${tableId}`, JSON.stringify(seats));
        }
        localStorage.removeItem('productsToTransfer');
        localStorage.removeItem('combinationsToTransfer');
        localStorage.removeItem('transferFromTable');
        navigate('/tables');
        setIsTableActionsPopupOpen(false);
    }

    const handleDeleteOrderClick = () => {
        setIsTableActionsPopupOpen(false);
        setShowDeleteOrderConfirmation(true);
    }

    const handleDeleteOrder = async () => {
        if (!activeOrder) return;

        try {
            setShowDeleteOrderConfirmation(false);
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/delete/${activeOrder.id}`, {
                method: 'GET',
                credentials: 'include',
            });

            if (response.ok) {
                setSuccessTitle("Success!");
                setSuccessContent("Order was deleted successfully!");
                setShowSuccess(true);
                setShowSpinner(false);
                setTimeout(() => {
                    setShowSuccess(false);
                    navigate('/tables');
                }, 2000);
            } else {
                setShowSpinner(false);
                setErrorTitle("Error");
                setErrorContent("Failed to delete order. Please try again.");
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                }, 3000);
            }
        } catch (error) {
            setShowSpinner(false);
            setErrorTitle("Error");
            setErrorContent("An error occurred while deleting the order.");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
    }

    const handleTransferProducts = () => {
        if (!hasPerm('pos-app.order.transfer')) return;
        localStorage.setItem('productsToTransfer', JSON.stringify(selectedProductIds));
        localStorage.setItem('combinationsToTransfer', JSON.stringify(selectedCombinationIds));
        localStorage.setItem('transferFromTable', JSON.stringify(activeOrder?.tableId));
        localStorage.removeItem('orderToMerge');
        localStorage.removeItem('tableToMergeFrom');
        navigate('/tables');
    }

    const openTableActions = () => {
        setIsTableActionsPopupOpen(true);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function getTextColorBasedOnBgColor(bgColor: string): string {
        if (!bgColor) return '#FFFFFF';

        const color = bgColor.startsWith('#') ? bgColor.slice(1) : bgColor;

        const r = parseInt(color.slice(0, 2), 16);
        const g = parseInt(color.slice(2, 4), 16);
        const b = parseInt(color.slice(4, 6), 16);

        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    }

    const getSubtotal = () => {
        const productsSubtotal = orderProducts.reduce((subtotal, ordProduct) => {
            // Check live seatResults first, fall back to saved mealPlanStatus
            if (ordProduct.seatId && seatResults[ordProduct.seatId]) {
                const ev = seatResults[ordProduct.seatId].evaluations?.find(e => e.orderProductId === ordProduct.id);
                if (ev) {
                    if (ev.status === 'included') return subtotal;
                } else if ((ordProduct as any).mealPlanStatus === 'included') return subtotal;
            } else if ((ordProduct as any).mealPlanStatus === 'included') return subtotal;
            return subtotal + (getProductPriceValue(ordProduct) * ordProduct.qty);
        }, 0);

        const combinationsSubtotal = (activeOrder?.combinations || []).reduce((subtotal, combination) => {
            return subtotal + getCombinationPriceValue(combination);
        }, 0);

        return productsSubtotal + combinationsSubtotal;
    }

    const getSubtotalFromUpdatedOrder = (currentOrder: Order) => {
        const productsSubtotal = currentOrder.products.reduce((subtotal, ordProduct) => {
            if ((ordProduct as any).mealPlanStatus === 'included') return subtotal;
            return subtotal + (getProductPriceValue(ordProduct) * ordProduct.qty);
        }, 0);

        const combinationsSubtotal = (currentOrder?.combinations || []).reduce((subtotal, combination) => {
            if ((combination as any).mealPlanStatus === 'included') return subtotal;
            return subtotal + getCombinationPriceValue(combination);
        }, 0);

        return productsSubtotal + combinationsSubtotal;
    }

    const getFormattedSubtotal = () => {
        const total = getSubtotal();
        return total.toFixed(2);
    }

    const getDiscount = () => {
        return orderProducts.reduce((totalDiscount, ordProduct) => {
            return totalDiscount + getDiscountValue(ordProduct) * ordProduct.qty;
        }, 0);
    }

    const getTax = () => {
        const productsTax = orderProducts.reduce((totalTax, ordProduct) => {
            if (!ordProduct.product) return totalTax;
            // Check live seatResults first, fall back to saved mealPlanStatus
            if (ordProduct.seatId && seatResults[ordProduct.seatId]) {
                const ev = seatResults[ordProduct.seatId].evaluations?.find(e => e.orderProductId === ordProduct.id);
                if (ev) {
                    if (ev.status === 'included') return totalTax;
                } else if ((ordProduct as any).mealPlanStatus === 'included') return totalTax;
            } else if ((ordProduct as any).mealPlanStatus === 'included') return totalTax;

            const { taxRate, taxType, price } = ordProduct.product;
            let taxAmount = taxType === 'percentage'
                ? (taxRate / 100) * price * ordProduct.qty
                : taxRate * ordProduct.qty;

            if (taxType === 'percentage') {
                taxAmount = parseFloat(taxAmount.toFixed(2));
            }

            return totalTax + taxAmount;
        }, 0);

        const combinationsTax = activeOrder?.combinations?.reduce((totalComboTax, combination) => {
            const { taxRate, taxType, price } = combination;
            let comboTaxAmount = taxType === 'percentage'
                ? (taxRate / 100) * price
                : taxRate;

            if (taxType === 'percentage') {
                comboTaxAmount = parseFloat(comboTaxAmount.toFixed(2));
            }

            return totalComboTax + comboTaxAmount;
        }, 0) || 0;

        return productsTax + combinationsTax;
    };


    const getTaxFromUpdatedOrder = (updatedOrder: Order) => {
        const productsTax = updatedOrder.products.reduce((totalTax, ordProduct) => {
            if (!ordProduct.product) return totalTax;
            if ((ordProduct as any).mealPlanStatus === 'included') return totalTax;

            const { taxRate, taxType, price } = ordProduct.product;
            let taxAmount = taxType === 'percentage'
                ? (taxRate / 100) * price * ordProduct.qty
                : taxRate * ordProduct.qty;

            if (taxType === 'percentage') {
                taxAmount = parseFloat(taxAmount.toFixed(2));
            }

            return totalTax + taxAmount;
        }, 0);

        const combinationsTax = updatedOrder?.combinations?.reduce((totalComboTax, combination) => {
            const { taxRate, taxType, price } = combination;
            let comboTaxAmount = taxType === 'percentage'
                ? (taxRate / 100) * price
                : taxRate;

            if (taxType === 'percentage') {
                comboTaxAmount = parseFloat(comboTaxAmount.toFixed(2));
            }

            return totalComboTax + comboTaxAmount;
        }, 0) || 0;

        return productsTax + combinationsTax;
    };


    const getTotal = () => {
        const subtotal = getSubtotal();
        const tax = getTax();
        const orderDiscount = getOrderDiscount() || 0;
        return subtotal + tax - orderDiscount;
    }

    const getTotalFromUpdatedOrder = (updatedOrder: Order) => {
        const subtotal = getSubtotalFromUpdatedOrder(updatedOrder);
        const tax = getTaxFromUpdatedOrder(updatedOrder);
        const orderDiscount = getOrderDiscountFromUpdatedOrder(updatedOrder) || 0;
        return subtotal + tax - orderDiscount;
    }

    const getFormattedTotal = () => {
        const total = getTotal();
        return total.toFixed(2);
    }

    const handleOverlayClick = () => {
        setSelectedProductToAdd(null);
        setIsAddAddonOpen(false);
        setIsAddVariantOpen(false);
    }

    const handleSelectedOrderDiscount = (discountType: string) => {
        if (activeOrder) {
            const updatedOrder: Order = {
                id: activeOrder.id ?? '',
                propertyId: activeOrder.propertyId ?? '',
                products: activeOrder.products ?? [],
                customerEmail: activeOrder.customerEmail ?? '',
                currency: activeOrder.currency ?? '',
                subtotal: activeOrder.subtotal ?? 0,
                total: activeOrder.total ?? 0,
                tax: activeOrder.tax ?? 0,
                isTaxPercentage: activeOrder.isTaxPercentage ?? false,
                discount: activeOrder.discount ?? 0,
                isOpen: activeOrder.isOpen ?? false,
                tableId: activeOrder.tableId ?? '',
                tableName: activeOrder.tableName ?? '',
                orderDiscountType: discountType,
                orderDiscountValue: activeOrder.orderDiscountValue ?? 0,
                status: activeOrder.status,
                orderNumber: activeOrder.orderNumber,
                customerName: activeOrder.customerName,
                // Rebuilt field-by-field, so anything omitted here is silently dropped
                // from the order. The customer link and the seats have to survive a discount.
                customerId: activeOrder.customerId,
                seats: activeOrder.seats,
                waiter: activeOrder.waiter,
                date: activeOrder.date,
                closedBy: activeOrder.closedBy,
                createdBy: activeOrder.createdBy,
                orderPaidOn: activeOrder.orderPaidOn,
                isVoided: activeOrder.isVoided,
                combinations: activeOrder.combinations,
                printer: null,
                mergedTablesIds: activeOrder.mergedTablesIds,
                mergedTablesNames: activeOrder.mergedTablesNames,
            };

            setActiveOrder(updatedOrder);
            setIsAddDiscountPopupOpen(false);
            setisOrderDiscountOpen(true);
            saveOrder(updatedOrder);
        }
    }

    const handleSaveOrderDiscountValue = (value: number, discountType: string, discountName?: string) => {
        if (activeOrder) {
            const updatedOrder: Order = {
                id: activeOrder.id ?? '',
                propertyId: activeOrder.propertyId ?? '',
                products: activeOrder.products ?? [],
                customerEmail: activeOrder.customerEmail ?? '',
                customerName: activeOrder.customerName,
                customerId: activeOrder.customerId,
                seats: activeOrder.seats,
                currency: activeOrder.currency ?? '',
                subtotal: activeOrder.subtotal ?? 0,
                tax: activeOrder.tax ?? 0,
                isTaxPercentage: activeOrder.isTaxPercentage ?? false,
                discount: activeOrder.discount ?? 0,
                isOpen: activeOrder.isOpen ?? false,
                tableId: activeOrder.tableId ?? '',
                tableName: activeOrder.tableName ?? '',
                orderDiscountType: discountType,
                orderDiscountValue: value ?? 0,
                orderDiscountName: discountName ?? '',
                status: activeOrder.status,
                waiter: activeOrder.waiter,
                orderNumber: activeOrder.orderNumber,
                total: activeOrder.total ?? 0,
                date: activeOrder.date,
                closedBy: activeOrder.closedBy,
                createdBy: activeOrder.createdBy,
                orderPaidOn: activeOrder.orderPaidOn,
                isVoided: activeOrder.isVoided,
                combinations: activeOrder.combinations,
                printer: null,
                mergedTablesIds: activeOrder.mergedTablesIds,
                mergedTablesNames: activeOrder.mergedTablesNames,
            };

            setisOrderDiscountOpen(false);
            setActiveOrder(updatedOrder);
            saveOrder(updatedOrder);
        }
    }

    // const handleSelectedAmountDiscountForCombination = () => {
    //     console.log("optionsOpenedForCombinationIndex: " + optionsOpenedForCombinationIndex)
    //     if (optionsOpenedForCombinationIndex != null) {
    //         const selectedCombination = activeOrder?.combinations[optionsOpenedForCombinationIndex];
    //         console.log("selectedCombination: " + selectedCombination)
    //         if (selectedCombination) {
    //             console.log("if ")
    //             const updatedCombination = { ...selectedCombination, discountType: 'amount' };
    //             console.log("updatedCombination: ");
    //             console.log(updatedCombination);
    //             console.log("optionsOpenedForCombinationIndex: " + optionsOpenedForCombinationIndex);
    //             setAddingDiscountForCombination(updatedCombination);
    //             setAddingDiscountForCombinationIndex(optionsOpenedForCombinationIndex);
    //             setIsOrderCombinationsOptionsOpen(false);
    //         }
    //     }
    // }

    const handleSelectedProductAmountDiscount = (index: number | null) => {
        if (index != null) {
            if (index >= 0 && index < orderProducts.length) {
                const selectedProduct = orderProducts[index];

                const updatedProduct = { ...selectedProduct, discountType: 'amount' };
                setAddingDiscountForProduct(updatedProduct);
                setAddingDiscountForProductIndex(index);

                const updatedProducts = [...orderProducts];
                updatedProducts[index] = updatedProduct;

                if (activeOrder) {
                    const updatedOrderWithProducts = {
                        ...activeOrder,
                        products: updatedProducts,
                    };
                    const updatedOrder = {
                        ...activeOrder,
                        products: updatedProducts,
                        subtotal: getSubtotalFromUpdatedOrder(updatedOrderWithProducts),
                        total: getTotalFromUpdatedOrder(updatedOrderWithProducts),
                        tax: getTaxFromUpdatedOrder(updatedOrderWithProducts),
                    };
                    setActiveOrder(updatedOrder);
                    saveOrder(updatedOrder);
                }

                setOrderProducts(prevOrderProducts => {
                    const updatedOrderProducts = [...prevOrderProducts];
                    updatedOrderProducts[index] = updatedProduct;
                    return updatedOrderProducts;
                });
            } else {
                console.error('Invalid product index');
            }
            setIsAddDiscountValuePopupOpen(true);
            setIsOrderOptionsOpen(false);
            setIsAddDiscountPopupOpen(false);
        }
    }

    const handleAddDiscountToItems = async (value: number, discountType: string, discountName?: string) => {
        if (activeOrder) {
            setIsDiscountPopupOpenForItems(false);
            setShowSpinner(true);
            const updatedProducts = activeOrder.products.map((product) =>
                selectedProductIds.includes(product.id)
                    ? { ...product, discountType, discount: value, discountName: discountName ?? '' }
                    : product
            );

            const updatedCombinations = activeOrder.combinations.map((combination) =>
                selectedCombinationIds.includes(combination.id)
                    ? { ...combination, discountType, discount: value, discountName: discountName ?? '' }
                    : combination
            );

            setOrderProducts(updatedProducts);
            const updatedOrder = {
                ...activeOrder,
                products: updatedProducts,
                combinations: updatedCombinations,
            }

            const updatedOrderWithPrices = {
                ...updatedOrder,
                subtotal: getSubtotalFromUpdatedOrder(updatedOrder),
                total: getTotalFromUpdatedOrder(updatedOrder),
                tax: getTaxFromUpdatedOrder(updatedOrder),
            }

            setActiveOrder(updatedOrderWithPrices);
            await saveOrder(updatedOrderWithPrices);
            setShowSpinner(false);
        }
    }

    // const handleSelectedProductPercentageDiscount = (index: number) => {
    //     if (index >= 0 && index < orderProducts.length) {
    //         const selectedProduct = orderProducts[index];

    //         const updatedProduct = { ...selectedProduct, discountType: 'percentage' };
    //         setAddingDiscountForProduct(updatedProduct);
    //         setAddingDiscountForProductIndex(index);

    //         const updatedProducts = [...orderProducts];
    //         updatedProducts[index] = updatedProduct;

    //         if (activeOrder) {
    //             const updatedOrderWithProducts = {
    //                 ...activeOrder,
    //                 products: updatedProducts,
    //             };
    //             const updatedOrder = {
    //                 ...activeOrder,
    //                 products: updatedProducts,
    //                 subtotal: getSubtotalFromUpdatedOrder(updatedOrderWithProducts),
    //                 total: getTotalFromUpdatedOrder(updatedOrderWithProducts),
    //                 tax: getTaxFromUpdatedOrder(updatedOrderWithProducts),
    //             };
    //             setActiveOrder(updatedOrder);
    //             saveOrder(updatedOrder);
    //         }

    //         setOrderProducts(prevOrderProducts => {
    //             const updatedOrderProducts = [...prevOrderProducts];
    //             updatedOrderProducts[index] = updatedProduct;
    //             return updatedOrderProducts;
    //         });
    //     } else {
    //         console.error('Invalid product index');
    //     }
    //     setIsAddDiscountValuePopupOpen(true);
    //     setIsAddDiscountPopupOpen(false);
    // }

    const closeAddDiscountValuePopup = () => {
        setAddingDiscountForProduct(null);
        setAddingDiscountForProductIndex(null);
        setIsAddDiscountValuePopupOpen(false);
    }

    const closeAddDiscountForCombination = () => {
        setAddingDiscountForCombination(null);
        setAddingDiscountForCombinationIndex(null);
    }

    const handleCombinationDiscountValueSaved = (value: number, discountType: string) => {
        if (addingDiscountForCombinationIndex != null) {
            const selectedCombination = activeOrder?.combinations[addingDiscountForCombinationIndex];
            if (selectedCombination) {
                const updatedCombination = { ...selectedCombination, discount: value, discountType: discountType };
                const updatedCombinations = [...activeOrder.combinations];
                updatedCombinations[addingDiscountForCombinationIndex] = updatedCombination;

                if (activeOrder) {
                    const updatedOrderWithProducts = {
                        ...activeOrder,
                        combinations: updatedCombinations,
                    };
                    const updatedOrder = {
                        ...activeOrder,
                        combinations: updatedCombinations,
                        subtotal: getSubtotalFromUpdatedOrder(updatedOrderWithProducts),
                        total: getTotalFromUpdatedOrder(updatedOrderWithProducts),
                        tax: getTaxFromUpdatedOrder(updatedOrderWithProducts),
                    };

                    setActiveOrder(updatedOrder);
                    saveOrder(updatedOrder);
                }
            }
        }
        closeAddDiscountForCombination();
    }

    const handleChangeTableSave = async (tableId: string, tableName: string) => {
        setSelectedNewTableId(tableId);
        setSelectedNewTableName(tableName);
        setIsChangeTableConfirmationOpen(true);
        setIsChangeTablePopupOpen(false);
    }

    const handleChangeTableConfirmation = async () => {
        if (activeOrder) {
            setShowSpinner(true);
            const updatedOrder = {
                ...activeOrder,
                tableId: selectedNewTableId,
                tableName: selectedNewTableName,
                mergedTablesIds: [],
                mergedTablesNames: []
            };

            setActiveOrder(updatedOrder);
            setIsChangeTableConfirmationOpen(false);
            await saveOrderWithNewTable(updatedOrder);
            setShowSpinner(false);
            setSuccessTitle('Success!');
            setSuccessContent('Table was changed successfully.');
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
            setSelectedNewTableId('');
            setSelectedNewTableName('');
        }
    }

    const handleDiscountValueSaved = (value: number, discountType: string, discountName?: string) => {
        if (addingDiscountForProductIndex != null) {
            const selectedProduct = orderProducts[addingDiscountForProductIndex];
            const updatedProduct = { ...selectedProduct, discount: value, discountType: discountType, discountName: discountName ?? '' };

            const updatedProducts = [...orderProducts];
            updatedProducts[addingDiscountForProductIndex] = updatedProduct;

            if (activeOrder) {
                const updatedOrderWithProducts = {
                    ...activeOrder,
                    products: updatedProducts,
                };
                const updatedOrder = {
                    ...activeOrder,
                    products: updatedProducts,
                    subtotal: getSubtotalFromUpdatedOrder(updatedOrderWithProducts),
                    total: getTotalFromUpdatedOrder(updatedOrderWithProducts),
                    tax: getTaxFromUpdatedOrder(updatedOrderWithProducts),
                };

                setActiveOrder(updatedOrder);
                saveOrder(updatedOrder);
            }

            setOrderProducts(prevOrderProducts => {
                const updatedOrderProducts = [...prevOrderProducts];
                updatedOrderProducts[addingDiscountForProductIndex] = updatedProduct;
                return updatedOrderProducts;
            });
        }
        closeAddDiscountValuePopup();
    }

    const calculateProductTotalPrice = (orderProduct: OrderProduct) => {
        // A selected variant's price REPLACES the base product price (not additive).
        const pricedVariant = orderProduct.variants?.find(v => v?.priceWithoutTax != null && v.priceWithoutTax !== 0);
        let totalPrice = pricedVariant?.priceWithoutTax ?? orderProduct.price;

        orderProduct.addons.forEach(addon => {
            if (addon.addon?.priceWithoutTax) {
                totalPrice += addon.addon?.priceWithoutTax * addon.qty;
            }
        });

        return totalPrice;
    };

    const getDiscountValue = (orderProduct: OrderProduct) => {
        if (orderProduct.discount && orderProduct.discount != 0) {
            const productTotalPrice = calculateProductTotalPrice(orderProduct);
            if (orderProduct.discountType == "amount")
                return orderProduct.discount;
            else if (orderProduct.discountType == "percentage")
                return (orderProduct.discount / 100) * productTotalPrice;
        }
        return 0;
    }

    const getOrderDiscount = () => {
        if (activeOrder) {
            let discount = 0;
            if (activeOrder.orderDiscountType == "percentage") {
                const subtotal = getSubtotal();
                discount = (activeOrder.orderDiscountValue / 100) * subtotal;
                discount = parseFloat(discount.toFixed(2));
                return discount;
            }
            else if (activeOrder.orderDiscountType == "amount") {
                return activeOrder.orderDiscountValue;
            }
        }
    }

    const getOrderDiscountFromUpdatedOrder = (updatedOrder: Order) => {
        if (updatedOrder) {
            let discount = 0;
            if (updatedOrder.orderDiscountType == "percentage") {
                const subtotal = getSubtotal();
                discount = (updatedOrder.orderDiscountValue / 100) * subtotal;
                discount = parseFloat(discount.toFixed(2));
                return discount;
            }
            else if (updatedOrder.orderDiscountType == "amount") {
                return updatedOrder.orderDiscountValue;
            }
        }
    }

    const handleDeleteCombination = (index: number) => {
        const newCombinations = activeOrder?.combinations.filter((_, i) => i !== index);
        if (activeOrder && newCombinations) {
            const updatedOrderWithProducts = {
                ...activeOrder,
                combinations: newCombinations,
            };
            const updatedOrder = {
                ...activeOrder,
                combinations: newCombinations,
                subtotal: getSubtotalFromUpdatedOrder(updatedOrderWithProducts),
                total: getTotalFromUpdatedOrder(updatedOrderWithProducts),
                tax: getTaxFromUpdatedOrder(updatedOrderWithProducts),
            };
            saveOrder(updatedOrder);
            setActiveOrder(updatedOrder);
        }
    };

    const [showDeleteOrder, setShowDeleteOrder] = useState<{ [key: number]: boolean }>({});
    const [swipedDistanceOrder, setSwipedDistanceOrder] = useState(0);
    const [showDeleteCombination, setShowDeleteCombination] = useState<{ [key: number]: boolean }>({});
    const [swipedDistanceCombination, setSwipedDistanceCombination] = useState(0);
    const resetShowDeleteOrder = () => setShowDeleteOrder({});
    const resetShowDeleteCombination = () => setShowDeleteCombination({});

    const handleSendProductsClick = () => {
        if (!hasPerm('pos-app.kitchen.send')) return;
        if (!orderHasNoProducts() || hasUnsentProducts()) {
            setIsSendProductsPopupOpen(true);
        }
    }

    // const sendOrderToKitchen = async () => {
    //     try {
    //         setShowSpinner(true);
    //         const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/send-order-to-kitchen/` + activeOrder?.id, {
    //             method: 'GET',
    //             credentials: 'include',
    //         });
    //         if (!response.ok) {
    //             throw new Error('Error fetching floor');
    //         }
    //         else {
    //             setSuccessTitle('Success!');
    //             setSuccessContent('Order was sent to the kitchen.');
    //             setShowSuccess(true);

    //             if (activeOrder) {
    //                 const updatedProducts = activeOrder.products?.map((product) => ({
    //                     ...product,
    //                     sentToKitchen: true,
    //                 }));

    //                 const updatedCombinations = activeOrder.combinations?.map((combination) => ({
    //                     ...combination,
    //                     sentToKitchen: true,
    //                 }));

    //                 setActiveOrder({
    //                     ...activeOrder,
    //                     products: updatedProducts,
    //                     combinations: updatedCombinations,
    //                 });
    //             }

    //             setTimeout(() => {
    //                 setShowSuccess(false);
    //             }, 3000);
    //         }

    //     } catch (error) {
    //         console.error("Error fetching data: ", error);
    //     }
    //     finally {
    //         setShowSpinner(false);
    //     }
    // }

    const orderHasNoProducts = () => {
        if (activeOrder) {
            const hasProds = activeOrder?.products == null || activeOrder?.products.length == 0 || orderProducts == null || orderProducts.length == 0;
            const hasCombi = activeOrder?.combinations == null || activeOrder?.combinations.length == 0;
            return hasProds && hasCombi;
        }
    }

    const orderHasNoSelectedProducts = () => {
        const hasProds = selectedProductIds == null || selectedProductIds.length == 0;
        const hasCombi = selectedCombinationIds == null || selectedCombinationIds.length == 0;
        return hasProds && hasCombi;
    }

    const hasUnsentProducts = () => {
        const hasUnsyncedProducts = activeOrder?.products?.some(
            (product) => product.sentToKitchen === false
        );

        const hasUnsyncedProductsOnOrderProd = orderProducts?.some(
            (product) => product.sentToKitchen === false
        );

        const hasUnsyncedCombinations = activeOrder?.combinations?.some(
            (combination) => combination.sentToKitchen === false
        );

        return hasUnsyncedCombinations || hasUnsyncedProductsOnOrderProd || hasUnsyncedProducts;
    };

    const unsentCount = (): number => {
        const prods = (orderProducts ?? []).filter(p => p.sentToKitchen === false).length;
        const combos = (activeOrder?.combinations ?? []).filter(c => c.sentToKitchen === false).length;
        return prods + combos;
    };

    const hasSentProducts = (): boolean => {
        const prods = (orderProducts ?? []).some(p => p.sentToKitchen);
        const combos = (activeOrder?.combinations ?? []).some(c => c.sentToKitchen);
        return prods || combos;
    };

    const handleReprintKitchenTicket = async () => {
        const sentIds = (orderProducts ?? []).filter(p => p.sentToKitchen).map(p => p.id);
        setIsPrintRowOpen(false);
        if (sentIds.length === 0) {
            setErrorTitle('Nothing sent yet');
            setErrorContent('There is no kitchen ticket to reprint for this order.');
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
            return;
        }
        try {
            const { printKitchenTickets } = await import('../services/KitchenPrintService');
            await printKitchenTickets({ order: activeOrder ?? undefined, floor, productIds: sentIds });
            setSuccessTitle('Reprint Sent');
            setSuccessContent('The kitchen ticket was sent to the printer again.');
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch {
            setErrorTitle('Reprint Failed');
            setErrorContent('Failed to reprint the kitchen ticket. Please try again.');
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
        }
    };

    // const formatDate = (dateString: string) => {
    //     const date = new Date(dateString);
    //     return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
    // };

    const styles = StyleSheet.create({
        container: {
            flex: 1,
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            paddingLeft: 15,
            flexDirection: 'row',
            display: 'flex',
            height: windowSize.height - 55,
            overflow: 'hidden'
        },
        productsContainerList: {
            height: windowSize.height - 212,
            display: 'flex',
            flexDirection: 'row',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            maxWidth: '100%',
            alignContent: 'flex-start',
            overflowY: 'auto' as const,
        },
        orderProductsContainer: {
        },
        searchInputContainer: {
            display: 'flex',
            alignItems: 'center',
            borderWidth: 1,
            borderColor: '#D0D5DD',
            borderStyle: 'solid',
            paddingLeft: 5,
            height: 30
        },
        iconContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingRight: 8
        },
        favoritesContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            height: windowSize.height - 162,
        },
        orderContainer: {
            display: 'flex',
            flexDirection: 'column',
            height: windowSize.height - 52,
            width: windowSize.width * 0.4,
        },
        categoriesList: {
            width: windowSize.width * 0.6
        },
        emptyRow: {
            height: 40,
            color: 'transparent'
        },
        grayRow: {
            backgroundColor: '#F9FAFB',
        },
        whiteRow: {
            backgroundColor: '#ffffff',
        },
        overlay: {
            backgroundColor: 'rgba(51, 51, 51, 0.6)',

            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10
        },
        searchContainer: {
            position: 'relative',
        },
        searchInput: {
            borderColor: '#D0D5DD',
            borderStyle: 'solid',
            borderWidth: 1,
            paddingLeft: 30,
            width: '45%',
            minWidth: 150
        },
        searchIcon: {
            position: 'absolute',
            left: 10,
            top: 15
        }
    });
    const [isPayNowEnabled, setIsPayNowEnabled] = useState(false);

    useEffect(() => {
        const checkIfAllItemsSentToKitchen = () => {
            const products = activeOrder?.products ?? [];
            const combinations = activeOrder?.combinations ?? [];

            console.log("Products:", products);
            console.log("Combinations:", combinations);

            const allProductsSent = products.length > 0 ? products.every(product => product.sentToKitchen) : false;
            const allCombinationsSent = combinations.length > 0 ? combinations.every(combination => combination.sentToKitchen) : true;

            console.log("All products sent:", allProductsSent);
            console.log("All combinations sent:", allCombinationsSent);

            setIsPayNowEnabled(allProductsSent && allCombinationsSent && (products.length > 0 || combinations.length > 0));
        };

        checkIfAllItemsSentToKitchen();
    }, [activeOrder]);

    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
    const [selectedCombinationIds, setSelectedCombinationIds] = useState<string[]>([]);

    const handleToggleCheckbox = (productId: string) => {
        setSelectedProductIds((prevState) =>
            prevState.includes(productId)
                ? prevState.filter((id) => id !== productId)
                : [...prevState, productId]
        );
    };

    const handleToggleCheckboxForCombination = (combinationId: string) => {
        setSelectedCombinationIds((prevState) =>
            prevState.includes(combinationId)
                ? prevState.filter((id) => id !== combinationId)
                : [...prevState, combinationId]
        );
    };

    const cancelProductsOptions = () => {
        setSelectedProductIds([]);
        setIsOrderOptionsOpen(false);
        setSelectedCombinationIds([]);
        setIsOrderCombinationsOptionsOpen(false);
        setOptionsOpenedForCombinationIndex(null);
    }

    const [editingItemProduct, setEditingItemProduct] = useState<OrderProduct | null>(null);
    const [editingItemCombination, setEditingItemCombination] = useState<OrderCombination | null>(null);
    const [deletingFromItemProduct, setdeletingFromItemProduct] = useState<OrderProduct | null>(null);
    const [deletingFromItemCombination, setdeletingFromItemCombination] = useState<OrderCombination | null>(null);

    const handleEditClick = () => {
        if (areMultipleProductsSelected() || !areAnyProductsSelected() || !canEditSelectedItem()) return;

        if (selectedProductIds != null && selectedProductIds.length > 0) {
            const product = activeOrder?.products.find(x => x.id == selectedProductIds[0]);
            if (product) {
                setEditingItemProduct(product);
                setEditingItemCombination(null);
            }
        }

        if (selectedCombinationIds != null && selectedCombinationIds.length > 0) {
            const combination = activeOrder?.combinations.find(x => x.id == selectedCombinationIds[0]);
            if (combination) {
                setEditingItemProduct(null);
                setEditingItemCombination(combination);
            }
        }
    }

    const handleDeleteClick = () => {
        if (areMultipleProductsSelected() || !areAnyProductsSelected() || !canDeleteSelectedItem()) return;

        if (selectedProductIds != null && selectedProductIds.length > 0) {
            const product = activeOrder?.products.find(x => x.id == selectedProductIds[0]);
            if (product) {
                setdeletingFromItemProduct(product);
                setdeletingFromItemCombination(null);
            }
        }

        if (selectedCombinationIds != null && selectedCombinationIds.length > 0) {
            const combination = activeOrder?.combinations.find(x => x.id == selectedCombinationIds[0]);
            if (combination) {
                setdeletingFromItemProduct(null);
                setdeletingFromItemCombination(combination);
            }
        }
    }

    const [editingDiscountForProduct, setEditingDiscountForProduct] = useState<OrderProduct | null>(null);
    const [editingDiscountForCombination, setEditingDiscountForCombination] = useState<OrderCombination | null>(null);
    const [editingNoteForProduct, setEditingNoteForProduct] = useState<OrderProduct | null>(null);
    const [editingNoteForCombination, setEditingNoteForCombination] = useState<OrderCombination | null>(null);
    const [isEditingProductsForCombination, setIsEditingProductsForCombination] = useState<OrderCombination | null>(null);

    const handleEditDiscount = () => {
        if (editingItemProduct) {
            setEditingDiscountForProduct(editingItemProduct);
            setEditingDiscountForCombination(null);
            setEditingItemProduct(null);
        }
        else if (editingItemCombination) {
            setEditingDiscountForCombination(editingItemCombination);
            setEditingDiscountForProduct(null);
            setEditingItemCombination(null);
        }
    }

    const handleEditNote = () => {
        if (editingItemProduct) {
            setEditingNoteForProduct(editingItemProduct);
            setEditingNoteForCombination(null);
            setEditingItemProduct(null);
        }
        else if (editingItemCombination) {
            setEditingNoteForCombination(editingItemCombination);
            setEditingNoteForProduct(null);
            setEditingItemCombination(null);
        }
    }

    const handleEditingProductsForFlexibleComb = () => {
        console.log("editingItemCombination")
        console.log(editingItemCombination)

        if (editingItemCombination) {

            setIsEditingProductsForCombination(editingItemCombination);
            setEditingItemCombination(null);
        }
    }

    const handleSaveEditDiscount = async (value: number, discountType: string, discountName?: string) => {
        if (activeOrder) {
            setShowSpinner(true);
            const updatedProducts = activeOrder.products.map((product) =>
                editingDiscountForProduct && editingDiscountForProduct.id == product.id
                    ? { ...product, discountType: discountType, discount: value, discountName: discountName ?? '' }
                    : product
            );

            const updatedCombinations = activeOrder.combinations.map((combination) =>
                editingDiscountForCombination && editingDiscountForCombination.id == combination.id
                    ? { ...combination, discountType: discountType, discount: value, discountName: discountName ?? '' }
                    : combination
            );

            setEditingDiscountForCombination(null);
            setEditingDiscountForProduct(null);

            setOrderProducts(updatedProducts);
            const updatedOrder = {
                ...activeOrder,
                products: updatedProducts,
                combinations: updatedCombinations,
            }

            const updatedOrderWithPrices = {
                ...updatedOrder,
                subtotal: getSubtotalFromUpdatedOrder(updatedOrder),
                total: getTotalFromUpdatedOrder(updatedOrder),
                tax: getTaxFromUpdatedOrder(updatedOrder),
            }

            setActiveOrder(updatedOrderWithPrices);
            await saveOrder(updatedOrderWithPrices);
            setShowSpinner(false);
        }
    }

    const handleDeleteDiscount = async () => {
        if (activeOrder) {
            setShowSpinner(true);
            const updatedProducts = activeOrder.products.map((product) =>
                deletingFromItemProduct && deletingFromItemProduct.id == product.id
                    ? { ...product, discountType: '', discount: 0 }
                    : product
            );

            const updatedCombinations = activeOrder.combinations.map((combination) =>
                deletingFromItemCombination && deletingFromItemCombination.id == combination.id
                    ? { ...combination, discountType: '', discount: 0 }
                    : combination
            );

            setdeletingFromItemCombination(null);
            setdeletingFromItemProduct(null);

            setOrderProducts(updatedProducts);
            const updatedOrder = {
                ...activeOrder,
                products: updatedProducts,
                combinations: updatedCombinations,
            }

            const updatedOrderWithPrices = {
                ...updatedOrder,
                subtotal: getSubtotalFromUpdatedOrder(updatedOrder),
                total: getTotalFromUpdatedOrder(updatedOrder),
                tax: getTaxFromUpdatedOrder(updatedOrder),
            }

            setActiveOrder(updatedOrderWithPrices);
            await saveOrder(updatedOrderWithPrices);
            setShowSpinner(false);
            setSuccessTitle('Success!');
            setSuccessContent('Discount was removed successfully.');
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
    }

    const handleDeleteAddons = async () => {
        if (activeOrder) {
            setShowSpinner(true);
            const updatedProducts = activeOrder.products.map((product) =>
                deletingFromItemProduct && deletingFromItemProduct.id == product.id
                    ? { ...product, addons: [] }
                    : product
            );

            setdeletingFromItemCombination(null);
            setdeletingFromItemProduct(null);

            setOrderProducts(updatedProducts);
            const updatedOrder = {
                ...activeOrder,
                products: updatedProducts,
            }

            const updatedOrderWithPrices = {
                ...updatedOrder,
                subtotal: getSubtotalFromUpdatedOrder(updatedOrder),
                total: getTotalFromUpdatedOrder(updatedOrder),
                tax: getTaxFromUpdatedOrder(updatedOrder),
            }

            setActiveOrder(updatedOrderWithPrices);
            await saveOrder(updatedOrderWithPrices);
            setShowSpinner(false);
            setSuccessTitle('Success!');
            setSuccessContent('Addons removed successfully.');
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
    }

    const handleDeleteVariants = async () => {
        if (activeOrder) {
            setShowSpinner(true);
            const updatedProducts = activeOrder.products.map((product) =>
                deletingFromItemProduct && deletingFromItemProduct.id == product.id
                    ? { ...product, variants: [] }
                    : product
            );

            setdeletingFromItemCombination(null);
            setdeletingFromItemProduct(null);

            setOrderProducts(updatedProducts);
            const updatedOrder = {
                ...activeOrder,
                products: updatedProducts,
            }

            const updatedOrderWithPrices = {
                ...updatedOrder,
                subtotal: getSubtotalFromUpdatedOrder(updatedOrder),
                total: getTotalFromUpdatedOrder(updatedOrder),
                tax: getTaxFromUpdatedOrder(updatedOrder),
            }

            setActiveOrder(updatedOrderWithPrices);
            await saveOrder(updatedOrderWithPrices);
            setShowSpinner(false);
            setSuccessTitle('Success!');
            setSuccessContent('Variants removed successfully.');
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
    }

    const handleSaveEditNote = async (note: string) => {
        if (activeOrder) {
            setShowSpinner(true);
            const updatedProducts = activeOrder.products.map((product) =>
                editingNoteForProduct && editingNoteForProduct.id == product.id ? { ...product, note: note } : product
            );

            const updatedCombinations = activeOrder.combinations.map((combination) =>
                editingNoteForCombination && editingNoteForCombination.id == combination.id ? { ...combination, note: note } : combination
            );

            setEditingNoteForCombination(null);
            setEditingNoteForProduct(null);
            setOrderProducts(updatedProducts);
            const updatedOrder = {
                ...activeOrder,
                products: updatedProducts,
                combinations: updatedCombinations,
            };
            setActiveOrder(updatedOrder);
            await saveOrder(updatedOrder);
            setShowSpinner(false);
        }
    };

    const handleDeleteNote = async () => {
        if (activeOrder) {
            setShowSpinner(true);
            const updatedProducts = activeOrder.products.map((product) =>
                deletingFromItemProduct && deletingFromItemProduct.id == product.id ? { ...product, note: '' } : product
            );

            const updatedCombinations = activeOrder.combinations.map((combination) =>
                deletingFromItemCombination && deletingFromItemCombination.id == combination.id ? { ...combination, note: '' } : combination
            );

            setdeletingFromItemCombination(null);
            setdeletingFromItemProduct(null);
            setOrderProducts(updatedProducts);
            const updatedOrder = {
                ...activeOrder,
                products: updatedProducts,
                combinations: updatedCombinations,
            };
            setActiveOrder(updatedOrder);
            await saveOrder(updatedOrder);
            setShowSpinner(false);
            setSuccessTitle('Success!');
            setSuccessContent('Note was removed successfully.');
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
    }

    const handleEditAddons = () => {
        setEditingItemCombination(null);
        setEditingItemProduct(null);
        handleAddAddonForProduct()
    }

    const handleEditVariants = () => {
        setEditingItemCombination(null);
        setEditingItemProduct(null);
        handleAddVariantForProduct();
    }

    const getCombinationBasedOnOrderCombinationId = (combId: string) => {
        console.log(combinations);
        console.log(combId);
        const combi = combinations.find(c => c.id == combId);
        return combi;
    }

    const buildSeatFromSearchResult = (result: CombinedSearchResult, seatNumber: number): Seat | null => {
        const baseId = `seat-${Date.now()}`;
        if (result.type === 'prostay' && result.prostayGuest) {
            const g = result.prostayGuest;
            return {
                id: baseId,
                seatNumber,
                type: 'hotel_guest',
                guestName: `${g.firstName || ''} ${g.lastName || ''}`.trim(),
                roomNumber: g.roomNumber,
                roomWithStatusId: g.roomWithSttausId,
                reservationNumber: g.reservationNumber,
                reservationId: g.reservationId,
                rateId: g.rateId,
                rateName: g.rateName,
                checkInDate: g.reservationCheckinDate,
                checkOutDate: g.reservationCheckoutDate,
            };
        }
        if (result.type === 'tableview' && result.customer) {
            const c = result.customer;
            const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Customer';
            return {
                id: baseId,
                seatNumber,
                type: 'walk_in',
                guestName: name,
                customerId: c.id,
            };
        }
        return null;
    };

    const handleAddCustomerToOrder = async (customerEmail: string, customerName: string, roomNumber: string, reservationNumber: string, result?: CombinedSearchResult) => {
        // If the table setup dialog is open, forward the selection into the dialog
        // instead of attaching the guest directly to the order.
        if (showTableSetup && result) {
            setPendingDialogGuest(result);
            setSearchMenuOpen(false);
            return;
        }

        // Dialog is closed: add the guest directly to the seats of the active order.
        if (activeOrder && result) {
            setShowSpinner(true);
            setSearchMenuOpen(false);

            // Find the first "empty" seat (a default walk-in with no real guest name)
            // to fill, otherwise append a new seat.
            const isDefaultEmpty = (s: Seat) =>
                s.type === 'walk_in' && !s.customerId &&
                (!s.guestName || /^Guest\s+\d+$/i.test(s.guestName));
            const targetIdx = seats.findIndex(isDefaultEmpty);

            let newSeats: Seat[];
            const newSeat = buildSeatFromSearchResult(result, targetIdx >= 0 ? seats[targetIdx].seatNumber : seats.length + 1);
            if (!newSeat) {
                setShowSpinner(false);
                return;
            }
            if (targetIdx >= 0) {
                newSeats = [...seats];
                newSeats[targetIdx] = { ...newSeats[targetIdx], ...newSeat, id: seats[targetIdx].id };
            } else {
                newSeats = [...seats, newSeat];
            }
            setSeats(newSeats);
            if (!activeSeatId && newSeats.length > 0) {
                setActiveSeatId(newSeats[0].id);
            }

            const firstHotelGuest = newSeats.find(s => s.type === 'hotel_guest' && s.reservationNumber);
            const hasHotelGuest = newSeats.some(s => s.type === 'hotel_guest');
            const resNum = firstHotelGuest?.reservationNumber || (hasHotelGuest ? (activeOrder.prostayReservationNumber || '') : '');
            const roomNum = firstHotelGuest?.roomNumber || (hasHotelGuest ? (activeOrder.roomNumber || '') : '');
            const orderRoomAllocations = newSeats
                .filter(s => s.type === 'hotel_guest' && s.reservationId && s.roomNumber && s.roomWithStatusId)
                .map(s => ({
                    reservationId: s.reservationId!,
                    roomNumber: s.roomNumber!,
                    roomWithStatusId: s.roomWithStatusId!,
                }));

            // If a TableView customer was selected, apply their discounts to existing products.
            const newDiscounts = result.type === 'tableview' && result.customer
                ? mergeCustomerDiscounts([result.customer])
                : [];
            const existingDiscounts = activeOrder.customerDiscounts || [];
            const combinedDiscounts: CustomerDiscount[] = [...existingDiscounts];
            const seenDiscountIds = new Set(existingDiscounts.map(d => d.id).filter(Boolean));
            for (const d of newDiscounts) {
                if (d.id && seenDiscountIds.has(d.id)) continue;
                if (d.id) seenDiscountIds.add(d.id);
                combinedDiscounts.push(d);
            }
            const updatedProducts = newDiscounts.length > 0
                ? applyCustomerDiscountsToProducts(activeOrder.products || [], newDiscounts)
                : (activeOrder.products || []);

            const updatedOrder = {
                ...activeOrder,
                seats: newSeats,
                prostayReservationNumber: resNum,
                roomNumber: roomNum,
                customerEmail: result.type === 'tableview' ? (result.customer?.email || activeOrder.customerEmail) : (result.email || activeOrder.customerEmail),
                customerName: newSeats.filter(s => s.guestName).map(s => s.guestName).join(', ') || activeOrder.customerName,
                customerId: (result.type === 'tableview' ? result.customer?.id : undefined)
                    || newSeats.find(s => s.customerId)?.customerId
                    || activeOrder.customerId,
                orderRoomAllocations,
                products: updatedProducts,
                customerDiscounts: combinedDiscounts,
            };
            setActiveOrder(updatedOrder);
            await saveOrder(updatedOrder);

            setShowSpinner(false);
            setSuccessTitle('Success!');
            setSuccessContent('Customer was added successfully.');
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
    }

    // ── Charge-to-Room: in-flow PMS guest search, attach, and folio lookup ──────
    // These power the redesigned Charge to Room flow inside the Receipt drawer so
    // the cashier can find/attach an in-house guest at pay time and run a credit
    // check before posting to the folio.
    const searchHotelGuestsForRoomCharge = async (term: string): Promise<ProstayGuestDto[]> => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/search-prostay-guest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currentPage: 1, searchTerm: term, pageSize: 50, filterDuplicatesByEmail: false }),
                credentials: 'include',
            });
            const data = await response.json();
            return (data.data || []) as ProstayGuestDto[];
        } catch (e) {
            console.error('charge-to-room guest search failed', e);
            return [];
        }
    };

    const attachHotelGuestForRoomCharge = async (guest: ProstayGuestDto): Promise<void> => {
        await handleAddCustomerToOrder(
            guest.email || '',
            `${guest.firstName || ''} ${guest.lastName || ''}`.trim(),
            guest.roomNumber || '',
            guest.reservationNumber || '',
            {
                type: 'prostay',
                firstName: guest.firstName,
                lastName: guest.lastName,
                email: guest.email,
                roomNumber: guest.roomNumber,
                reservationNumber: guest.reservationNumber,
                reservationStatus: guest.reservationStatus,
                id: guest.id,
                prostayGuest: guest,
            },
        );
    };

    const fetchReservationFolio = async (reservationNumber: string): Promise<{ balance: number; creditLimit: number } | null> => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/reservation-folio/${encodeURIComponent(reservationNumber)}`, { credentials: 'include' });
            const data = await response.json();
            const f = data?.data;
            if (!f) return null;
            return { balance: f.balance ?? 0, creditLimit: f.creditLimit ?? 0 };
        } catch (e) {
            return null;
        }
    };

    // Assigns order lines to a seat. Scope picks the lines: explicit ids, only
    // currently-unassigned lines, or all lines. Updating orderProducts re-triggers
    // the meal-plan evaluation effect so allowances recompute for the target seat.
    const assignItemsToSeat = async (
        seatId: string,
        scope: { productIds?: string[]; combinationIds?: string[]; onlyUnassigned?: boolean } = {},
    ): Promise<void> => {
        if (!activeOrder) return;
        const matchProd = (p: OrderProduct) =>
            scope.productIds ? scope.productIds.includes(p.id)
                : scope.onlyUnassigned ? !p.seatId
                    : true;
        const matchComb = (c: OrderCombination) =>
            scope.combinationIds ? scope.combinationIds.includes(c.id)
                : scope.onlyUnassigned ? !c.seatId
                    : true;

        const updatedProducts = orderProducts.map(p => (matchProd(p) ? { ...p, seatId } : p));
        const updatedCombinations = (activeOrder.combinations || []).map(c => (matchComb(c) ? { ...c, seatId } : c));

        setOrderProducts(updatedProducts);
        const updatedOrder = { ...activeOrder, products: updatedProducts, combinations: updatedCombinations };
        setActiveOrder(updatedOrder);
        await saveOrder(updatedOrder);
    };

    const handleConfirmPutOnRoomSelection = async (selectedRes: ProstayGuestDto | null) => {
        setIsPutOnRoomOpen(false);
        setIsReceiptPopupOpen(false);
        if (!activeOrder) return;

        try {
            setShowSpinner(true);

            const updatedOrder = { ...activeOrder, customerName: selectedRes?.firstName + " " + selectedRes?.lastName, customerEmail: selectedRes?.email };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/put-on-room`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    reservationNumber: selectedRes?.reservationNumber,
                    order: updatedOrder,
                }),
                credentials: 'include',
            });

            setShowSpinner(false);

            if (response.ok) {
                autoPrintReceipt();
                setSuccessTitle('Success!');
                setSuccessContent('Order was closed successfully. You will be redirected to the orders page.');
                setShowSuccess(true);

                setTimeout(() => {
                    setShowSuccess(false);
                    navigate('/orders');
                }, 3000);
            } else {
                const failure = await readRoomChargeFailure(response, 'Failed to close order');
                setErrorTitle(failure.checkedOut ? GUEST_CHECKED_OUT_TITLE : 'Error');
                setErrorContent(failure.message);
                setShowError(true);
                setTimeout(() => {
                    setShowError(false);
                }, failure.checkedOut ? GUEST_CHECKED_OUT_TOAST_MS : 3000);
            }
        } catch (error: any) {
            setShowSpinner(false);
            setErrorTitle('Unexpected Error');
            setErrorContent(error.message || 'Something went wrong while trying to close the order.');
            setShowError(true);
        }
    };
    const visibleCombinations = useMemo(() => {
        const activeCategoryIds = new Set(
            categories.filter(c => c.isActive).map(c => c.id)
        );
    
        return combinations.filter(comb => {
            if (comb.type === 0) {
                // Fixed: check each subproduct's product categoryId
                return comb.subproducts?.every(sp => {
                    if (!sp.productId) return true; // null productId, skip
                    const product = products.find(p => p.id === sp.productId);
                    return product ? activeCategoryIds.has(product.categoryId) : true;
                }) ?? true;
            } else {
                // Flexible: check products inside each subproductCategory
                return comb.subproductCategories?.every(sc =>
                    sc.products?.every(p => {
                        if (!p.productId) return true; // null productId, skip
                        const product = products.find(pr => pr.id === p.productId);
                        return product ? activeCategoryIds.has(product.categoryId) : true;
                    }) ?? true
                ) ?? true;
            }
        });
    }, [combinations, categories, products]);

    // const getStatusColor = (status: string) => {
    //     if (status == 'Canceled') {
    //         return 'rgb(255, 75, 85)';
    //     }
    //     else if (status == 'Confirmed') {
    //         return 'rgb(39, 174, 96)';
    //     }
    //     else if (status == 'In House') {
    //         return 'rgb(215, 119, 119)';
    //     }
    //     else if (status == 'Pending') {
    //         return 'rgb(150, 215, 210)';
    //     }
    //     else {
    //         return 'rgb(56, 56, 56)'
    //     }
    // }

    return (
        <div className="to-pos ps-scope app-fixed-surface">
            {orderViewers.length > 0 && (
                <div style={{
                    position: 'fixed',
                    top: 64,
                    left: '35%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#FFF8E1',
                    border: '1px solid #FFD54F',
                    borderRadius: 20,
                    padding: '4px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: '#5D4037',
                    zIndex: 100,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                    whiteSpace: 'nowrap',
                }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#5D4037" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#5D4037" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {orderViewers.length === 1
                        ? <span><b>{orderViewers[0].displayName}</b> is also viewing this order</span>
                        : <span>Also open on <b>{orderViewers.length}</b> other device{orderViewers.length > 1 ? 's' : ''}</span>
                    }
                </div>
            )}
            {/* 1 — Vertical category rail */}
            <nav className='to-rail' aria-label='Product categories'>
                <button type='button' onClick={() => { setIsCombinationsSelected(true); setSelectedCategory(undefined) }} className={`to-cat${isCombinationsSelected ? ' is-active' : ''}`}>
                    <span className='to-cat__badge'>
                        <svg width="36" height="36" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'relative', zIndex: 1 }}>
                            <circle cx="24.5" cy="24.5" r="24.5" fill="#1643E0" />
                            <path d="M24.808 11.2196C24.2975 10.9763 23.7045 10.9763 23.1941 11.2196C22.7528 11.43 22.5095 11.7953 22.3868 11.9957C22.26 12.2029 22.1298 12.4668 22.0028 12.7241L19.3082 18.1832L13.2806 19.0642C12.9969 19.1056 12.7057 19.1481 12.4696 19.2049C12.2411 19.2598 11.8188 19.3786 11.4826 19.7334C11.0938 20.1439 10.9109 20.7079 10.9849 21.2684C11.0489 21.753 11.3212 22.097 11.4739 22.2756C11.6318 22.4601 11.8427 22.6654 12.0481 22.8654L16.4079 27.1118L15.3791 33.1096C15.3306 33.3924 15.2808 33.6825 15.2616 33.9248C15.243 34.159 15.2251 34.5977 15.4585 35.0273C15.7284 35.5243 16.2083 35.8729 16.7644 35.976C17.2451 36.0651 17.6567 35.9124 17.8738 35.8224C18.0982 35.7293 18.3587 35.5922 18.6127 35.4586L24.001 32.6249L29.3894 35.4586C29.6433 35.5922 29.9038 35.7293 30.1282 35.8224C30.3453 35.9124 30.7569 36.0651 31.2377 35.976C31.7937 35.8729 32.2736 35.5243 32.5435 35.0273C32.7769 34.5977 32.759 34.159 32.7404 33.9248C32.7212 33.6826 32.6714 33.3925 32.6229 33.1097L31.5942 27.1118L35.954 22.8654C36.1594 22.6653 36.3702 22.4601 36.5281 22.2756C36.6808 22.097 36.9531 21.753 37.0171 21.2684C37.0911 20.7079 36.9083 20.1439 36.5194 19.7334C36.1833 19.3786 35.7609 19.2598 35.5324 19.2049C35.2963 19.1481 35.0051 19.1056 34.7214 19.0642L28.6938 18.1832L25.9992 12.7241C25.8723 12.4669 25.7421 12.2029 25.6152 11.9957C25.4925 11.7953 25.2492 11.43 24.808 11.2196Z" fill="white" />
                        </svg>
                    </span>
                    <span className='to-cat__label'>Combos</span>
                </button>
                {
                    categories.filter(cat => cat.isActive).map((cat) =>
                        <button type='button' key={cat.id || cat.name} onClick={() => handleCategoryClick(cat)} className={`to-cat${selectedCategory?.name == cat.name ? ' is-active' : ''}`}>
                            <span className='to-cat__badge'>
                                <img src={cat.imageUrl} alt='' onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                                <span className='to-cat__fallback'>{cat.name?.charAt(0)?.toUpperCase() || '?'}</span>
                            </span>
                            <span className='to-cat__label'>{cat.name}</span>
                        </button>
                    )
                }
            </nav>

            {/* 2 — Product browser */}
            <section className='to-main'>
                <div className='to-main__bar to-search'>
                    <input
                        className="to-search__input"
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search products"
                    />
                    <svg className="to-search__icon" width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.5002 16.5002L12.881 12.881M12.881 12.881C13.5001 12.2619 13.9912 11.527 14.3262 10.7181C14.6612 9.90927 14.8337 9.04234 14.8337 8.16684C14.8337 7.29134 14.6612 6.42441 14.3262 5.61555C13.9912 4.80669 13.5001 4.07174 12.881 3.45267C12.2619 2.8336 11.527 2.34252 10.7181 2.00748C9.90927 1.67244 9.04234 1.5 8.16684 1.5C7.29134 1.5 6.42441 1.67244 5.61555 2.00748C4.80669 2.34252 4.07174 2.8336 3.45267 3.45267C2.2024 4.70295 1.5 6.39868 1.5 8.16684C1.5 9.93499 2.2024 11.6307 3.45267 12.881C4.70295 14.1313 6.39868 14.8337 8.16684 14.8337C9.93499 14.8337 11.6307 14.1313 12.881 12.881Z" stroke="#667085" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <div className='to-grid hide-scrollbar'>
                    {
                        catalogFailed && !catalogLoading && (
                            <div className='to-grid__error'>
                                <span>Couldn't load the menu. Check your connection and try again.</span>
                                <button type='button' onClick={() => setCatalogReloadKey(k => k + 1)}>Retry</button>
                            </div>
                        )
                    }
                    {
                        !isCombinationsSelected && favorites.filter(x => x.isVisible).length > 0 && (
                            <>
                                <div className='to-grid__label'>Favorites</div>
                                <div className='to-grid__row'>
                                    {favorites.filter(x => x.isVisible).map((fav) =>
                                        <div className='product to-tile to-tile--fav' onClick={() => handleProductClick(fav)} style={{ ['--tile-accent' as any]: fav.color || undefined }}>
                                            <span className='to-tile__name'>
                                                {fav.name.length > 15 ? `${fav.name.slice(0, 15)}...` : fav.name}
                                            </span>
                                            <span className='to-tile__price'>{formatPrice(fav.price)}</span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )
                    }
                    <div className='to-grid__label'>{isCombinationsSelected ? 'Combinations' : (selectedCategory?.name || 'All items')}</div>
                    <div className='to-grid__row'>
                        {
                            !isCombinationsSelected &&
                            filteredProducts.filter(x => x.isVisible).map((prod) =>
                                <div className='product to-tile' onClick={() => handleProductClick(prod)} style={{ ['--tile-accent' as any]: prod.color || '#1643E0' }}>
                                    <span className='to-tile__name'>
                                        {prod.name.length > 15 ? `${prod.name.slice(0, 15)}...` : prod.name}
                                    </span>
                                    <span className='to-tile__price'>{formatPrice(prod.price)}</span>
                                </div>)
                        }
                        {
                            isCombinationsSelected &&
                            visibleCombinations.map((comb) =>
                                <div className='product to-tile to-tile--combo' onClick={() => handleCombinationClick(comb)}>
                                    <span className='to-tile__name'>
                                        {comb.name.length > 15 ? `${comb.name.slice(0, 15)}...` : comb.name}
                                    </span>
                                    <span className='to-tile__price'>{formatPrice(comb.price)}</span>
                                </div>
                            )
                        }
                    </div>
                </div>
            </section>

            {/* 3 — Order ticket */}
            <div className='to-folio'>
              {/* Fixed header: table/date line, seats, column header */}
              <div className='to-folio__top'>
                <div className='to-folio__head'>
                    <div className='to-folio__heading'>
                        <div className='to-folio__titlewrap'>
                            <span className='to-folio__title'>Guest Check</span>
                            <span className='to-folio__sub'>
                                {activeOrder?.tableName || getTableNoById()}
                                {activeOrder?.customerName ? " · " + activeOrder?.customerName + ((activeOrder?.roomNumber && seats.some(s => s.type === 'hotel_guest')) ? " · " + activeOrder?.roomNumber : "") : ""}
                            </span>
                        </div>
                        {(orderProducts.length > 0 || (activeOrder?.combinations?.length || 0) > 0) && (
                            <span className='to-folio__count'>{orderProducts.reduce((s, p) => s + (p.qty || 1), 0) + (activeOrder?.combinations?.length || 0)} items</span>
                        )}
                    </div>
                </div>

                {/* Seat Selector */}
                {seats.length > 0 && (
                    <SeatSelector
                        seats={seats}
                        activeSeatId={activeSeatId}
                        onSelectSeat={setActiveSeatId}
                        onAddSeat={handleAddSeat}
                        onRemoveSeat={handleRemoveSeat}
                        itemCountPerSeat={itemCountPerSeat}
                    />
                )}

                {/* Allowance Tracker for active hotel guest seat */}
                {activeSeatId && seats.length > 0 && (() => {
                    const activeSeat = seats.find(s => s.id === activeSeatId);
                    if (activeSeat && activeSeat.type === 'hotel_guest') {
                        return (
                            <AllowanceTracker
                                seat={activeSeat}
                                result={seatResults[activeSeatId] || null}
                            />
                        );
                    }
                    return null;
                })()}

                <div className='to-cols d-flex gap-0'>
                    <div className='col p-2'>
                        <span>ID</span>
                    </div>
                    <div className='col py-2'>
                        <span>QTY</span>
                    </div>
                    <div className='col-6 py-2 '>
                        <span>Product Name</span>
                    </div>
                    <div className='col py-2 d-flex justify-content-end' style={{ paddingRight: 10 }}>
                        <span>Price</span>
                    </div>
                </div>
              </div>{/* end fixed header */}

              {/* Scrollable receipt items */}
              <div className='to-folio__scroll'>
                <div style={{ ...styles.orderProductsContainer }}>
                    {
                        <div ref={(el) => { if (el) { const h = el.getBoundingClientRect().height; if (Math.abs(h - totalHeight) > 1) setTotalHeight(h); } }}>
                            {orderProducts && orderProducts.length > 0 && (() => {
                                // ── Build display groups ────────────────────────────
                                // Group identical-looking rows together for display only.
                                // Storage stays per-item so the meal-plan engine evaluates
                                // each row independently.
                                interface DisplayGroup {
                                    rep: OrderProduct;
                                    indices: number[];
                                    ids: string[];
                                    qty: number;
                                    mealPlanStatus: 'included' | 'extra' | 'none';
                                }
                                const groups: DisplayGroup[] = [];
                                orderProducts.forEach((p, idx) => {
                                    let status: 'included' | 'extra' | 'none' = 'none';
                                    if (p.seatId && seatResults[p.seatId]) {
                                        const ev = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id);
                                        if (ev) status = ev.status;
                                    }
                                    // Normalize "free" rows: a row is visually "free" if it's
                                    // included by the engine OR has been waived. Both render
                                    // with the green MP badge and Rp0, so they group together.
                                    const isWaived = !!(p as any).mealPlanOverride?.waived;
                                    const visualStatus: 'included' | 'extra' | 'none' = isWaived ? 'included' : status;

                                    const matchIdx = groups.findIndex(g => {
                                        const r = g.rep;
                                        if (r.productId !== p.productId) return false;
                                        if ((r.seatId || undefined) !== (p.seatId || undefined)) return false;
                                        if (!!r.sentToKitchen !== !!p.sentToKitchen) return false;
                                        if ((r.note || '') !== (p.note || '')) return false;
                                        if ((r.addons?.length || 0) > 0 || (p.addons?.length || 0) > 0) return false;
                                        if ((r.variants?.length || 0) > 0 || (p.variants?.length || 0) > 0) return false;
                                        if ((r.discount || 0) !== (p.discount || 0)) return false;
                                        if ((r.discountType || '') !== (p.discountType || '')) return false;
                                        if (g.mealPlanStatus !== visualStatus) return false;
                                        return true;
                                    });
                                    if (matchIdx >= 0) {
                                        groups[matchIdx].indices.push(idx);
                                        groups[matchIdx].ids.push(p.id);
                                        groups[matchIdx].qty += p.qty || 1;
                                    } else {
                                        groups.push({ rep: p, indices: [idx], ids: [p.id], qty: p.qty || 1, mealPlanStatus: visualStatus });
                                    }
                                });

                                const handleGroupDelete = (groupIdsSet: Set<string>) => {
                                    const newProductList = orderProducts.filter(p => !groupIdsSet.has(p.id));
                                    setOrderProducts(newProductList);
                                    if (activeOrder) {
                                        const updatedOrderWithProducts = { ...activeOrder, products: newProductList };
                                        const updatedOrder = {
                                            ...activeOrder,
                                            products: newProductList,
                                            subtotal: getSubtotalFromUpdatedOrder(updatedOrderWithProducts),
                                            total: getTotalFromUpdatedOrder(updatedOrderWithProducts),
                                            tax: getTaxFromUpdatedOrder(updatedOrderWithProducts),
                                        };
                                        setActiveOrder(updatedOrder);
                                        saveOrder(updatedOrder);
                                    }
                                };

                                return (
                                    <>
                                        {groups.map((group, gIdx) => {
                                            const displayProd: OrderProduct = { ...group.rep, qty: group.qty };
                                            const groupIdsSet = new Set(group.ids);
                                            const groupSelected = group.ids.every(id => selectedProductIds.includes(id));
                                            return (
                                                <SwipeableOrderItem
                                                    prod={displayProd}
                                                    index={gIdx}
                                                    showDelete={showDeleteOrder}
                                                    key={`group-${group.ids.join('-')}`}
                                                    backgroundColor={gIdx % 2 === 0 ? '#ffffff' : '#F9FAFB'}
                                                    setShowDelete={setShowDeleteOrder}
                                                    swipedDistance={swipedDistanceOrder}
                                                    setSwipedDistance={setSwipedDistanceOrder}
                                                    handleLongPress={handleLongPress}
                                                    handleCancelLongPress={handleCancelLongPress}
                                                    onDelete={() => handleGroupDelete(groupIdsSet)}
                                                    resetShowDeleteCombination={resetShowDeleteCombination}
                                                    displayCheckbox={isOrderOptionsOpen || isOrderCombinationsOptionsOpen}
                                                    onToggleCheckbox={() => {
                                                        // Toggle every underlying id together
                                                        group.ids.forEach(id => handleToggleCheckbox(id));
                                                    }}
                                                    checkboxState={groupSelected}
                                                    mealPlanStatus={group.mealPlanStatus}
                                                    onAddNote={() => {
                                                        setSelectedCombinationIds([]);
                                                        setSelectedProductIds(group.ids);
                                                        setIsAddNotePopupOpen(true);
                                                    }}
                                                />
                                            );
                                        })}
                                    </>
                                );
                            })()}
                            {activeOrder?.combinations && activeOrder.combinations.length > 0 && (
                                <>
                                    {activeOrder.combinations.map((comb, index) => {
                                        const globalIndex = orderProducts.length + index;
                                        return (
                                            <SwipeableCombinationItem
                                                key={index}
                                                comb={comb}
                                                index={index}
                                                backgroundColor={globalIndex % 2 === 0 ? '#ffffff' : '#F9FAFB'}
                                                showDelete={showDeleteCombination}
                                                setShowDelete={setShowDeleteCombination}
                                                swipedDistance={swipedDistanceCombination}
                                                setSwipedDistance={setSwipedDistanceCombination}
                                                handleLongPress={handleCombinationLongPress}
                                                handleCancelLongPress={handleCancelLongPress}
                                                onDelete={handleDeleteCombination}
                                                resetShowDeleteOrder={resetShowDeleteOrder}
                                                displayCheckbox={isOrderOptionsOpen || isOrderCombinationsOptionsOpen}
                                                onToggleCheckbox={() => handleToggleCheckboxForCombination(comb.id)}
                                                checkboxState={selectedCombinationIds.includes(comb.id)}
                                                onAddNote={() => {
                                                    setSelectedProductIds([]);
                                                    setSelectedCombinationIds([comb.id]);
                                                    setIsAddNotePopupOpen(true);
                                                }}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    }
                    {Array.from({ length: emptyRowCount }, (_, index) => {
                        const globalIndex = orderProducts.length + (activeOrder?.combinations?.length || 0) + index;
                        return (
                            <div
                                key={index}
                                className='to-emptyrow'
                                style={{
                                    ...styles.emptyRow,
                                    backgroundColor: globalIndex % 2 === 0 ? '#ffffff' : '#F9FAFB',
                                    borderTop: '1px solid #EAECF0'
                                }}
                            >
                                <div className='col-2'>Row</div>
                            </div>
                        );
                    })}

                </div>
              </div>{/* end scrollable receipt items */}

              {/* Fixed totals footer */}
              <div className='to-folio__totals'>
                    <div className='to-totals'>
                        <div className='d-flex justify-content-between gap-0' style={{ borderTop: '1px solid #F4F4F4' }}>
                            <div className='col p-2' style={{ backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '14px' }}>Subtotal</span>
                            </div>
                            <div className='col-auto px-2 py-2' style={{ backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex', justifyContent: 'end' }}>{formatPrice(getFormattedSubtotal())}</span>
                            </div>
                        </div>
                        {
                            (activeOrder?.orderDiscountValue && activeOrder?.orderDiscountValue > 0) ?
                                <div className='to-totals__disc d-flex justify-content-between gap-0' style={{ borderTop: '1px solid #F4F4F4' }}>
                                    <div className='col p-2' style={{ backgroundColor: '#fff' }}>
                                        <span style={{ fontSize: '14px' }}>{activeOrder?.orderDiscountName || 'Discount'} {activeOrder?.orderDiscountType == "percentage" ? "(" + activeOrder?.orderDiscountValue + "%)" : ""}</span>
                                    </div>
                                    <div className='col-auto px-2 py-2' style={{ backgroundColor: '#fff' }}>
                                        <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex', justifyContent: 'end', whiteSpace: 'nowrap' }}>{`-${formatPrice(getOrderDiscount())}`}</span>
                                    </div>
                                </div>
                                :
                                <>
                                </>
                        }
                        <div className='d-flex justify-content-between gap-0' style={{ borderTop: '1px solid #F4F4F4' }}>
                            <div className='col p-2' style={{ backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '14px' }}>Tax</span>
                            </div>
                            <div className='col-auto px-2 py-2' style={{ backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '14px', textAlign: 'end', display: 'flex', justifyContent: 'end' }}>{formatPrice(getTax())}</span>
                            </div>
                        </div>
                        <div className='to-totals__total d-flex justify-content-between gap-0'>
                            <div className='col p-2' style={{ backgroundColor: '#EAECF0' }}>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#667085' }}>Total</span>
                            </div>
                            <div className='col-auto px-2 py-2' style={{ backgroundColor: '#EAECF0' }}>
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#667085', textAlign: 'end', display: 'flex', justifyContent: 'end' }}>{formatPrice(getFormattedTotal())}</span>
                            </div>
                        </div>
                    </div>
              </div>{/* end fixed totals */}

                {!(isOrderOptionsOpen || isOrderCombinationsOptionsOpen) && (
                    <div className='to-paybar'>
                        <button className={isPayNowEnabled ? 'blue-filled-btn d-flex align-items-center w-100' : 'blue-filled-btn d-flex align-items-center w-100'} onClick={openReceipt} disabled={!hasPerm('pos-app.order.create')} style={{ opacity: hasPerm('pos-app.order.create') ? 1 : 0.5 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 9h20M5.2 5h13.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 6.52 22 7.08 22 8.2v7.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 19 19.92 19 18.8 19H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 17.48 2 16.92 2 15.8V8.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 5 4.08 5 5.2 5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M6 15h4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Pay Now
                        </button>
                    </div>
                )}
                {
                    isOrderOptionsOpen || isOrderCombinationsOptionsOpen ?
                        //here
                        hasPerm('pos-app.order.create') ?
                        <div className='tia-actionbar'>
                            <div className='tia-actionbar__head'>
                                <span className='tia-selcount'>{(selectedProductIds?.length || 0) + (selectedCombinationIds?.length || 0)} selected</span>
                                <button className='tia-done' onClick={cancelProductsOptions}>Done</button>
                            </div>
                            <div className='tia-actions'>
                                <button className='tia-action' disabled={areMultipleProductsSelected() || !areAnyProductsSelected() || !canEditSelectedItem()} onClick={() => handleEditClick()}>
                                    <span className='tia-action__ic'>
                                        <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.87601 17.1157C1.92195 16.7022 1.94493 16.4955 2.00748 16.3022C2.06298 16.1308 2.1414 15.9676 2.24061 15.8172C2.35242 15.6476 2.49952 15.5005 2.7937 15.2063L16 2.00006C17.1046 0.895488 18.8954 0.895489 20 2.00006C21.1046 3.10463 21.1046 4.89549 20 6.00006L6.7937 19.2063C6.49951 19.5005 6.35242 19.6476 6.18286 19.7594C6.03242 19.8586 5.86926 19.937 5.69782 19.9925C5.50457 20.0551 5.29783 20.0781 4.88434 20.124L1.49997 20.5001L1.87601 17.1157Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </span>
                                    Edit
                                </button>
                                <button className='tia-action' disabled={orderHasNoSelectedProducts()} onClick={() => { if (!orderHasNoSelectedProducts()) handleAddNoteForItem() }}>
                                    <span className='tia-action__ic'>
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 2H5.8C4.11984 2 3.27976 2 2.63803 2.32698C2.07354 2.6146 1.6146 3.07354 1.32698 3.63803C1 4.27976 1 5.11984 1 6.8V14.2C1 15.8802 1 16.7202 1.32698 17.362C1.6146 17.9265 2.07354 18.3854 2.63803 18.673C3.27976 19 4.11984 19 5.8 19H13.2C14.8802 19 15.7202 19 16.362 18.673C16.9265 18.3854 17.3854 17.9265 17.673 17.362C18 16.7202 18 15.8802 18 14.2V11M18.1213 1.87868C19.2929 3.05025 19.2929 4.94975 18.1213 6.12132C16.9497 7.29289 15.0503 7.29289 13.8787 6.12132C12.7071 4.94975 12.7071 3.05025 13.8787 1.87868C15.0503 0.707107 16.9497 0.707107 18.1213 1.87868Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </span>
                                    Note
                                </button>
                                {hasPerm('pos-app.discount.apply') && (
                                    <button className='tia-action' disabled={orderHasNoSelectedProducts()} onClick={() => { if (!orderHasNoSelectedProducts()) setIsDiscountPopupOpenForItems(true) }}>
                                        <span className='tia-action__ic'>
                                            <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 8H8.01M14 14H14.01M15 7L7 15M8.5 8C8.5 8.27614 8.27614 8.5 8 8.5C7.72386 8.5 7.5 8.27614 7.5 8C7.5 7.72386 7.72386 7.5 8 7.5C8.27614 7.5 8.5 7.72386 8.5 8ZM14.5 14C14.5 14.2761 14.2761 14.5 14 14.5C13.7239 14.5 13.5 14.2761 13.5 14C13.5 13.7239 13.7239 13.5 14 13.5C14.2761 13.5 14.5 13.7239 14.5 14ZM21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </span>
                                        Discount
                                    </button>
                                )}
                                {selectedCombinationIds?.length == 0 && selectedProductIds?.length == 1 && shouldDisplayAddVariant() && (
                                    <button className='tia-action' onClick={handleAddVariantForProduct}>
                                        <span className='tia-action__ic'>
                                            <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 4H10.9344C13.9816 4 15.5053 4 16.0836 4.54729C16.5836 5.02037 16.8051 5.71728 16.6702 6.39221C16.514 7.17302 15.2701 8.05285 12.7823 9.81253L8.71772 12.6875C6.2299 14.4471 4.98599 15.327 4.82984 16.1078C4.69486 16.7827 4.91642 17.4796 5.41636 17.9527C5.99474 18.5 7.51836 18.5 10.5656 18.5H11.5M7 4C7 5.65685 5.65685 7 4 7C2.34315 7 1 5.65685 1 4C1 2.34315 2.34315 1 4 1C5.65685 1 7 2.34315 7 4ZM21 18C21 19.6569 19.6569 21 18 21C16.3431 21 15 19.6569 15 18C15 16.3431 16.3431 15 18 15C19.6569 15 21 16.3431 21 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </span>
                                        Variants
                                    </button>
                                )}
                                {selectedCombinationIds?.length == 0 && selectedProductIds?.length == 1 && shouldDisplayAddAddon() && (
                                    <button className='tia-action' onClick={() => handleAddAddonForProduct()}>
                                        <span className='tia-action__ic'>
                                            <svg width="20" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 16V4C7 3.06812 7 2.60218 6.84776 2.23463C6.64477 1.74458 6.25542 1.35523 5.76537 1.15224C5.39782 1 4.93188 1 4 1C3.06812 1 2.60218 1 2.23463 1.15224C1.74458 1.35523 1.35523 1.74458 1.15224 2.23463C1 2.60218 1 3.06812 1 4V16C1 16.9319 1 17.3978 1.15224 17.7654C1.35523 18.2554 1.74458 18.6448 2.23463 18.8478C2.60218 19 3.06812 19 4 19C4.93188 19 5.39782 19 5.76537 18.8478C6.25542 18.6448 6.64477 18.2554 6.84776 17.7654C7 17.3978 7 16.9319 7 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M17 14V6C17 5.06812 17 4.60218 16.8478 4.23463C16.6448 3.74458 16.2554 3.35523 15.7654 3.15224C15.3978 3 14.9319 3 14 3C13.0681 3 12.6022 3 12.2346 3.15224C11.7446 3.35523 11.3552 3.74458 11.1522 4.23463C11 4.60218 11 5.06812 11 6V14C11 14.9319 11 15.3978 11.1522 15.7654C11.3552 16.2554 11.7446 16.6448 12.2346 16.8478C12.6022 17 13.0681 17 14 17C14.9319 17 15.3978 17 15.7654 16.8478C16.2554 16.6448 16.6448 16.2554 16.8478 15.7654C17 15.3978 17 14.9319 17 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </span>
                                        Add-ons
                                    </button>
                                )}
                                {hasPerm('pos-app.order.transfer') && (
                                    <button className='tia-action' disabled={orderHasNoSelectedProducts()} onClick={() => { if (!orderHasNoSelectedProducts()) handleTransferProducts() }}>
                                        <span className='tia-action__ic'>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 6C6.67157 6 6 6.67157 6 7.5C6 8.32843 6.67157 9 7.5 9H18.8787L6.43934 21.4393C5.85355 22.0251 5.85355 22.9749 6.43934 23.5607C7.02513 24.1464 7.97487 24.1464 8.56066 23.5607L21 11.1213V22.5C21 23.3284 21.6716 24 22.5 24C23.3284 24 24 23.3284 24 22.5V7.5C24 6.67157 23.3284 6 22.5 6H7.5Z" fill="currentColor" /></svg>
                                        </span>
                                        Transfer
                                    </button>
                                )}
                                <button className='tia-action tia-action--danger' disabled={areMultipleProductsSelected() || !areAnyProductsSelected() || !canDeleteSelectedItem()} onClick={handleDeleteClick}>
                                    <span className='tia-action__ic'>
                                        <svg width="20" height="20" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 5V4.2C14 3.0799 14 2.51984 13.782 2.09202C13.5903 1.71569 13.2843 1.40973 12.908 1.21799C12.4802 1 11.9201 1 10.8 1H9.2C8.07989 1 7.51984 1 7.09202 1.21799C6.71569 1.40973 6.40973 1.71569 6.21799 2.09202C6 2.51984 6 3.0799 6 4.2V5M8 10.5V15.5M12 10.5V15.5M1 5H19M17 5V16.2C17 17.8802 17 18.7202 16.673 19.362C16.3854 19.9265 15.9265 20.3854 15.362 20.673C14.7202 21 13.8802 21 12.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </span>
                                    Delete
                                </button>
                            </div>
                        </div>
                        : null
                        :
                        <div className='to-actionbar'>
                            {isPrintRowOpen ?
                                <div className='to-printrow'>
                                    <div className='to-printrow__head'>What would you like to print?</div>
                                    <div className='to-printrow__opts'>
                                        <button className='to-printopt' disabled={!hasSentProducts()} onClick={handleReprintKitchenTicket}>
                                            <span className='to-printopt__ic'>
                                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6v-8Z" /></svg>
                                            </span>
                                            <span className='to-printopt__t'>Reprint kitchen ticket</span>
                                        </button>
                                        <button className='to-printopt' disabled={orderHasNoProducts() || isPrinting} onClick={() => { setIsPrintRowOpen(false); handlePrintReceipt(); }}>
                                            <span className='to-printopt__ic'>
                                                {isPrinting
                                                    ? <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#1643E0' }} />
                                                    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>}
                                            </span>
                                            <span className='to-printopt__t'>Print bill</span>
                                        </button>
                                        <button className='to-printopt to-printopt--cancel' onClick={() => setIsPrintRowOpen(false)}>
                                            <span className='to-printopt__ic'>
                                                <svg width="20" height="20" viewBox="0 0 14 14" fill="none"><path d="M13 1 1 13M1 1l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </span>
                                            <span className='to-printopt__t'>Cancel</span>
                                        </button>
                                    </div>
                                </div>
                                :
                            <>
                            {hasPerm('pos-app.order.create') && <div className='col' style={{ width: '20%' }}>
                                <button className='large-blue-sq-btn w-100' onClick={() => setShowTableSetup(true)} style={{ backgroundColor: seats.length > 0 ? '#027A48' : '#1643E0' }}>
                                    <svg width="22" height="20" viewBox="0 0 28 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M26.2144 24.25V21.75C26.2144 19.4202 24.6209 17.4626 22.4644 16.9075M18.0894 2.11345C19.9217 2.85518 21.2144 4.65164 21.2144 6.75C21.2144 8.84836 19.9217 10.6448 18.0894 11.3866M19.9644 24.25C19.9644 21.9203 19.9644 20.7554 19.5838 19.8366C19.0763 18.6114 18.1029 17.6381 16.8778 17.1306C15.9589 16.75 14.7941 16.75 12.4644 16.75H8.71436C6.38465 16.75 5.21979 16.75 4.30094 17.1306C3.0758 17.6381 2.10243 18.6114 1.59496 19.8366C1.21436 20.7554 1.21436 21.9203 1.21436 24.25M15.5894 6.75C15.5894 9.51142 13.3508 11.75 10.5894 11.75C7.82793 11.75 5.58936 9.51142 5.58936 6.75C5.58936 3.98858 7.82793 1.75 10.5894 1.75C13.3508 1.75 15.5894 3.98858 15.5894 6.75Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <div className='btn-text'>
                                        {seats.length > 0 ? 'Seats' : 'Table Setup'}
                                    </div>
                                </button>
                            </div>}
                            {hasPerm('pos-app.order.create') && hasPerm('pos-app.kitchen.send') && <div className='col' style={{ width: '20%' }}>
                                <button className={orderHasNoProducts() ? 'large-blue-outline-sq-btn disabled w-100' : hasUnsentProducts() ? 'large-blue-outline-sq-btn  w-100' : 'large-blue-outline-sq-btn sent w-100'} onClick={() => handleSendProductsClick()} style={{ position: 'relative' }}>
                                    {!orderHasNoProducts() && hasUnsentProducts() && unsentCount() > 0 &&
                                        <span className='to-sendbadge'>{unsentCount()}</span>}
                                    <svg width="22" height="21" viewBox="0 0 27 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M11.2675 15.8752L24.3925 2.75022M11.4269 16.2853L14.7121 24.7328C15.0015 25.477 15.1462 25.8491 15.3547 25.9577C15.5354 26.0519 15.7508 26.052 15.9316 25.958C16.1402 25.8497 16.2854 25.4777 16.5757 24.7339L24.8136 3.62421C25.0756 2.95274 25.2066 2.617 25.135 2.40247C25.0727 2.21615 24.9265 2.06994 24.7402 2.0077C24.5257 1.93603 24.1899 2.06705 23.5185 2.32909L2.40879 10.567C1.66495 10.8573 1.29302 11.0024 1.18464 11.2111C1.09068 11.3919 1.0908 11.6072 1.18498 11.788C1.29361 11.9965 1.6657 12.1412 2.40988 12.4306L10.8574 15.7157C11.0084 15.7745 11.084 15.8039 11.1476 15.8492C11.204 15.8894 11.2532 15.9387 11.2935 15.9951C11.3388 16.0587 11.3682 16.1342 11.4269 16.2853Z" stroke={orderHasNoProducts() ? 'rgb(51, 51, 51)' : hasUnsentProducts() ? '#1643E0' : '#ffffff'} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                    <div className='btn-text'>
                                        {orderHasNoProducts() ? 'Send' : hasUnsentProducts() ? "Send" : "Sent"}
                                    </div>
                                </button>
                            </div>}
                            {hasPerm('pos-app.order.create') && <div className='col' style={{ width: '20%' }}>
                                <button className={orderHasNoProducts() || isPrinting ? 'large-blue-outline-sq-btn disabled w-100' : 'large-blue-outline-sq-btn w-100'} onClick={() => { if (!orderHasNoProducts() && !isPrinting) setIsPrintRowOpen(true); }}>
                                    {isPrinting ? (
                                        <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#1643E0' }} />
                                    ) : (
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M6 9V2H18V9M6 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H18M6 14H18V22H6V14Z" stroke={orderHasNoProducts() ? 'rgb(51, 51, 51)' : '#1643E0'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                    <div className='btn-text'>
                                        {isPrinting ? 'Printing...' : 'Print'}
                                    </div>
                                </button>
                            </div>}
                            {hasPerm('pos-app.order.create') && <div className='col' style={{ width: '20%' }}>
                                <button ref={tableActionsBtnRef} className='large-blue-outline-sq-btn w-100' onClick={openTableActions}>
                                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13 13L24.25 13M13 1.75L13 24.25M7.75 1.75H18.25C20.3502 1.75 21.4003 1.75 22.2025 2.15873C22.9081 2.51825 23.4817 3.09193 23.8413 3.79754C24.25 4.5997 24.25 5.6498 24.25 7.75V18.25C24.25 20.3502 24.25 21.4003 23.8413 22.2025C23.4817 22.9081 22.9081 23.4817 22.2025 23.8413C21.4003 24.25 20.3502 24.25 18.25 24.25H7.75C5.6498 24.25 4.5997 24.25 3.79754 23.8413C3.09193 23.4817 2.51825 22.9081 2.15873 22.2025C1.75 21.4003 1.75 20.3502 1.75 18.25V7.75C1.75 5.6498 1.75 4.5997 2.15873 3.79754C2.51825 3.09193 3.09193 2.51825 3.79754 2.15873C4.5997 1.75 5.6498 1.75 7.75 1.75Z" stroke="#1643E0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                    <div className='btn-text'>
                                        Table actions
                                    </div>
                                </button>
                            </div>}
                            </>}
                        </div>
                }
            </div>
            {
                isReceiptPopupOpen &&
                <>
                    <ReceiptPopup
                        isOpen={isReceiptPopupOpen}
                        orderProducts={orderProducts.map(p => { let ms = (p as any).mealPlanStatus || 'none'; if (p.seatId && seatResults[p.seatId]) { const ev = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id); if (ev) ms = ev.status; } return { ...p, mealPlanStatus: ms }; })}
                        onClose={() => setIsReceiptPopupOpen(false)}
                        activeOrder={activeOrder}
                        openCashPayment={(seatTotal?: number, reservationInfo?: { reservationId: string; reservationNumber: string; roomNumber: string; roomWithStatusId: string; seatId?: string; guestName?: string }) => {
                            if (reservationInfo) {
                                // Split bill: reservation info comes from the seat
                                setIsReceiptPopupOpen(false);
                                setSplitPaySeatTotal(seatTotal !== undefined ? seatTotal : capturedTotal);
                                setPaymentReservationInfo(reservationInfo);
                                setisCashPopupOpen(true);
                            } else {
                                // Non-split: check if multiple hotel guest reservations
                                const hotelSeats = seats.filter(s => s.type === 'hotel_guest' && s.reservationId);
                                const uniqueReservations = [...new Set(hotelSeats.map(s => s.reservationId))];
                                if (uniqueReservations.length > 1) {
                                    setIsReceiptPopupOpen(false);
                                    setSplitPaySeatTotal(seatTotal !== undefined ? seatTotal : capturedTotal);
                                    setPendingPaymentMethod('cash');
                                    setShowWhoPaysPopup(true);
                                } else {
                                    // Single or no reservation — use first hotel guest's info if available
                                    const firstHotel = hotelSeats[0];
                                    setIsReceiptPopupOpen(false);
                                    setSplitPaySeatTotal(seatTotal !== undefined ? seatTotal : capturedTotal);
                                    setPaymentReservationInfo(firstHotel ? { reservationId: firstHotel.reservationId!, reservationNumber: firstHotel.reservationNumber!, roomNumber: firstHotel.roomNumber!, roomWithStatusId: firstHotel.roomWithStatusId! } : undefined);
                                    setisCashPopupOpen(true);
                                }
                            }
                        }}
                        openCardPayment={(seatTotal?: number, reservationInfo?: { reservationId: string; reservationNumber: string; roomNumber: string; roomWithStatusId: string; seatId?: string; guestName?: string }) => {
                            if (reservationInfo) {
                                setIsReceiptPopupOpen(false);
                                setSplitPaySeatTotal(seatTotal !== undefined ? seatTotal : capturedTotal);
                                setPaymentReservationInfo(reservationInfo);
                                setisCardPopupOpen(true);
                            } else {
                                const hotelSeats = seats.filter(s => s.type === 'hotel_guest' && s.reservationId);
                                const uniqueReservations = [...new Set(hotelSeats.map(s => s.reservationId))];
                                if (uniqueReservations.length > 1) {
                                    setIsReceiptPopupOpen(false);
                                    setSplitPaySeatTotal(seatTotal !== undefined ? seatTotal : capturedTotal);
                                    setPendingPaymentMethod('card');
                                    setShowWhoPaysPopup(true);
                                } else {
                                    const firstHotel = hotelSeats[0];
                                    setIsReceiptPopupOpen(false);
                                    setSplitPaySeatTotal(seatTotal !== undefined ? seatTotal : capturedTotal);
                                    setPaymentReservationInfo(firstHotel ? { reservationId: firstHotel.reservationId!, reservationNumber: firstHotel.reservationNumber!, roomNumber: firstHotel.roomNumber!, roomWithStatusId: firstHotel.roomWithStatusId! } : undefined);
                                    setisCardPopupOpen(true);
                                }
                            }
                        }}
                        onBookOnRoom={() => {
                            setIsPutOnRoomOpen(true);
                        }}
                        showProductId={true}
                        seats={seats}
                        seatResults={seatResults}
                        onChargeToRoom={async (seat) => {
                            if (!activeOrder || !seat.reservationNumber) return;
                            try {
                                setShowSpinner(true);

                                // Calculate this seat's total to pay — discounted (chargeable base minus the
                                // seat's share of the order-level discount), matching the split receipt.
                                const seatToPay = getSeatToPay(seat, activeOrder, seats, orderProducts, activeOrder.combinations || [], seatResults);

                                // Add a payment record for this seat's room charge
                                const roomPayment = {
                                    id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
                                    amount: seatToPay,
                                    type: 'room_charge',
                                    date: new Date(),
                                    change: 0,
                                    balanceBefore: 0,
                                    confirmationNumber: '',
                                    cardType: '',
                                    reservationId: seat.reservationId,
                                    reservationNumber: seat.reservationNumber,
                                    roomNumber: seat.roomNumber,
                                    roomWithStatusId: seat.roomWithStatusId,
                                    seatId: seat.id,
                                    guestName: seat.guestName,
                                };
                                const updatedPayments = [...(activeOrder.payments || []), roomPayment];
                                const updatedOrder = {
                                    ...activeOrder,
                                    payments: updatedPayments,
                                };

                                // Post to Prostay folio with split receipt number
                                const seatIndex = seats.indexOf(seat) + 1;
                                const isSplit = seats.length > 1;
                                const splitOrderNumber = isSplit
                                    ? `${activeOrder.orderNumber}-${seatIndex}`
                                    : activeOrder.orderNumber;
                                // Send the LIVE product list stamped with meal-plan status (activeOrder.products
                                // can lag behind orderProducts). The API renders this seat's receipt from it in
                                // the same request that posts the charge, so the folio line and its receipt
                                // document are always created together.
                                const stampedProducts = orderProducts.map(p => {
                                    let ms = 'none';
                                    if (p.seatId && seatResults[p.seatId]) {
                                        const ev = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id);
                                        if (ev) ms = ev.status;
                                    }
                                    return { ...p, mealPlanStatus: ms };
                                });
                                const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/put-on-room`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        reservationNumber: seat.reservationNumber,
                                        order: { ...updatedOrder, products: stampedProducts, total: seatToPay, orderNumber: splitOrderNumber, roomNumber: seat.roomNumber, roomWithStatusId: seat.roomWithStatusId },
                                        ...(isSplit ? {
                                            seatId: seat.id,
                                            splitIndex: seatIndex,
                                            guestName: seat.guestName || '',
                                            formattedDate: new Date().toLocaleString('en-US', { weekday: 'short', month: 'long', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }),
                                        } : {}),
                                    }),
                                    credentials: 'include',
                                });

                                if (!response.ok) {
                                    setShowSpinner(false);
                                    const failure = await readRoomChargeFailure(response, 'Failed to charge to room');
                                    setErrorTitle(failure.checkedOut ? GUEST_CHECKED_OUT_TITLE : 'Error');
                                    setErrorContent(failure.message);
                                    setShowError(true);
                                    setTimeout(() => setShowError(false), failure.checkedOut ? GUEST_CHECKED_OUT_TOAST_MS : 3000);
                                    return;
                                }

                                // Check if all seats are now paid
                                const allSeatsPaid = seats.every(s => {
                                    if (s.type === 'walk_in') {
                                        // Walk-ins need cash/card, check payments
                                        const walkInTotal = orderProducts.filter(p => p.seatId === s.id).reduce((sum, p) => sum + p.price * p.qty, 0);
                                        if (walkInTotal === 0) return true;
                                        return updatedPayments.some(p => (p as any).seatId === s.id);
                                    }
                                    // Hotel guest: always require a payment record (even for 0-amount meal plan seats)
                                    // so each guest gets their folio transaction before the order closes
                                    const sProdTotal = orderProducts.filter(p => p.seatId === s.id).reduce((sum, p) => sum + p.price * p.qty, 0);
                                    const sCombTotal = (activeOrder.combinations?.filter(c => c.seatId === s.id) || []).reduce((sum, c) => sum + c.price, 0);
                                    const sTotal = sProdTotal + sCombTotal;
                                    const sIncl = seatResults[s.id]?.totalIncluded || 0;
                                    const sToPay = s.type === 'hotel_guest' ? sTotal - sIncl : sTotal;
                                    // Match by seatId (unique per guest) to avoid false matches when guests share a reservation
                                    return updatedPayments.some(p => (p as any).seatId === s.id && (Number(p.amount) - Number(p.change)) >= sToPay);
                                });

                                // Save the order with payment record (keep open if seats remain)
                                setActiveOrder(updatedOrder);
                                await saveOrder(updatedOrder);

                                // Burn this guest's allowance now that their bill is settled. The
                                // API records it too when the order closes; this keeps the local
                                // cache honest so the same device blocks a repeat order straight
                                // away, and the record is deduped server-side.
                                await recordMealConsumptionsForOrder(updatedOrder, [seat]);

                                setShowSpinner(false);
                                setSuccessTitle('Success!');
                                setSuccessContent(`Charged to room ${seat.roomNumber} (${seat.guestName}).`);
                                setShowSuccess(true);

                                if (allSeatsPaid) {
                                    // All seats paid — auto-print receipt and navigate away
                                    autoPrintReceipt(updatedOrder);
                                    setTimeout(() => { setShowSuccess(false); navigate('/orders'); }, 3000);
                                } else {
                                    // Other seats still need to pay — stay on the page
                                    setTimeout(() => setShowSuccess(false), 3000);
                                }
                            } catch (error: any) {
                                setShowSpinner(false);
                                setErrorTitle('Error');
                                setErrorContent(error.message || 'Something went wrong.');
                                setShowError(true);
                            }
                        }}
                        onSearchGuests={searchHotelGuestsForRoomCharge}
                        onAttachHotelGuest={attachHotelGuestForRoomCharge}
                        onFetchFolio={fetchReservationFolio}
                        onAssignItemsToSeat={assignItemsToSeat}
                        onRoomChargeComplete={(order?: Order) => {
                            autoPrintReceipt(order);
                            setIsReceiptPopupOpen(false);
                            navigate('/orders');
                        }}
                        onChargeOrderToRoom={async () => {
                            if (!activeOrder) return { ok: false, error: 'No active order' };
                            const hotelSeats = seats.filter(s => s.type === 'hotel_guest' && s.reservationNumber);
                            if (hotelSeats.length === 0) return { ok: false, error: 'No hotel guest attached' };

                            // Multiple hotel guests → defer to the existing "who pays" selection popup.
                            if (hotelSeats.length > 1) {
                                setIsReceiptPopupOpen(false);
                                setPendingPaymentMethod('room');
                                setShowWhoPaysPopup(true);
                                return { ok: false, redirected: true };
                            }

                            const seat = hotelSeats[0];
                            try {
                                setShowSpinner(true);
                                const orderTotal = getTotal();
                                const rawRef = (typeof crypto !== 'undefined' && crypto.randomUUID)
                                    ? crypto.randomUUID().replace(/-/g, '').slice(0, 6)
                                    : Math.random().toString(36).slice(2, 8);
                                const postingRef = `PST-${rawRef.toUpperCase()}`;

                                const roomPayment = {
                                    id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
                                    amount: orderTotal,
                                    type: 'room_charge',
                                    date: new Date(),
                                    change: 0,
                                    balanceBefore: 0,
                                    confirmationNumber: postingRef,
                                    cardType: '',
                                    reservationId: seat.reservationId,
                                    reservationNumber: seat.reservationNumber,
                                    roomNumber: seat.roomNumber,
                                    roomWithStatusId: seat.roomWithStatusId,
                                    guestName: seat.guestName,
                                };
                                const updatedOrder = {
                                    ...activeOrder,
                                    payments: [...(activeOrder.payments || []), roomPayment],
                                };

                                const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/put-on-room`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        reservationNumber: seat.reservationNumber,
                                        order: { ...updatedOrder, total: orderTotal, roomNumber: seat.roomNumber, roomWithStatusId: seat.roomWithStatusId },
                                    }),
                                    credentials: 'include',
                                });

                                if (!response.ok) {
                                    setShowSpinner(false);
                                    const failure = await readRoomChargeFailure(response, 'Failed to charge to room');
                                    return { ok: false, error: failure.message };
                                }

                                setActiveOrder(updatedOrder);
                                await saveOrder(updatedOrder);
                                // The whole bill is settled here, so every seat's allowance is
                                // spent (see the split path above for why this runs client-side
                                // as well as in the API).
                                await recordMealConsumptionsForOrder(updatedOrder);
                                setShowSpinner(false);
                                return { ok: true, reference: postingRef, amount: orderTotal, order: updatedOrder };
                            } catch (error: any) {
                                setShowSpinner(false);
                                return { ok: false, error: error.message || 'Something went wrong.' };
                            }
                        }}
                        onCloseZeroOrder={async () => {
                            setIsReceiptPopupOpen(false);
                            if (!activeOrder) return;
                            try {
                                setShowSpinner(true);
                                const prods = orderProducts.map(p => {
                                    let ms = (p as any).mealPlanStatus || 'none';
                                    if (p.seatId && seatResults[p.seatId]) {
                                        const ev = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id);
                                        if (ev) ms = ev.status;
                                    }
                                    return { ...p, mealPlanStatus: ms };
                                });
                                // Send directly with total=0, bypassing saveOrder's recalculation
                                await fetch(`${process.env.REACT_APP_API_URL}/Order/update`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        id: activeOrder.id,
                                        propertyId: activeOrder.propertyId,
                                        products: prods,
                                        customerEmail: activeOrder.customerEmail,
                                        customerName: activeOrder.customerName,
                                        customerId: activeOrder.customerId,
                                        seats,
                                        currency: activeOrder.currency,
                                        subtotal: 0,
                                        total: 0,
                                        tax: 0,
                                        isTaxPercentage: activeOrder.isTaxPercentage,
                                        discount: 0,
                                        isOpen: false,
                                        tableId: activeOrder.tableId,
                                        tableName: activeOrder.tableName,
                                        orderDiscountType: activeOrder.orderDiscountType,
                                        orderDiscountValue: activeOrder.orderDiscountValue,
                                        orderDiscountName: activeOrder.orderDiscountName,
                                        status: 3,
                                        orderNumber: activeOrder.orderNumber,
                                        waiter: activeOrder.waiter,
                                        closedBy: activeOrder.closedBy,
                                        createdBy: activeOrder.createdBy,
                                        orderPaidOn: new Date().toISOString(),
                                        isVoided: activeOrder.isVoided,
                                        combinations: activeOrder.combinations,
                                        subtotalWithoutDiscounts: activeOrder.subtotalWithoutDiscounts,
                                        payments: [{ id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9), amount: 0, type: 'meal_plan', date: new Date(), change: 0, balanceBefore: 0, confirmationNumber: '', cardType: '' }],
                                        mergedTablesIds: activeOrder.mergedTablesIds,
                                        mergedTablesNames: activeOrder.mergedTablesNames,
                                        roomNumber: activeOrder.roomNumber,
                                        // Don't send prostayReservationNumber when split bill — per-seat folio
                                        // transactions were already posted via put-on-room
                                        prostayReservationNumber: seats.length > 1 ? undefined : activeOrder.prostayReservationNumber,
                                    }),
                                    credentials: 'include',
                                });
                                localStorage.removeItem(`seats_${tableId}`);
                                // Record meal consumption for double-dip prevention
                                for (const seat of seats) {
                                    if (seat.type !== 'hotel_guest' || !seat.rateId) continue;
                                    const seatProds = prods.filter(p => p.seatId === seat.id && (p as any).mealPlanStatus === 'included');
                                    if (seatProds.length > 0) {
                                        const config = seat.rateId ? (seatConfigs[seat.rateId] || null) : null;
                                        // Use captured period from order creation, fall back to current time
                                        const currentPeriod = seat.capturedMealPeriod || (config ? mealPlanEngine.getCurrentMealPeriod(config) : null);
                                        if (currentPeriod) {
                                            await mealPlanEngine.recordConsumption(
                                                seat,
                                                currentPeriod,
                                                seatProds.map(p => ({
                                                    orderProductId: p.id,
                                                    category: (p as any).categoryName || '',
                                                    name: p.name,
                                                    price: p.price,
                                                    isIncludedInPlan: true,
                                                })),
                                                activeOrder.id,
                                            );
                                        }
                                    }
                                }
                                setShowSpinner(false);
                                autoPrintReceipt();
                                setSuccessTitle('Success!');
                                setSuccessContent('Order closed — all items covered by meal plan.');
                                setShowSuccess(true);
                                setTimeout(() => { setShowSuccess(false); navigate('/orders'); }, 3000);
                            } catch (error) {
                                setShowSpinner(false);
                                console.error(error);
                            }
                        }}
                    />
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => setIsReceiptPopupOpen(false)}></div>
                </>
            }

            {
                isCashPopupOpen &&
                <>
                    <CashPaymentPopup isOpen={isCashPopupOpen} onClose={() => { setisCashPopupOpen(false); setSplitPaySeatTotal(undefined); setPaymentReservationInfo(undefined); fetchOrder(); }} onPartialPayment={() => { if (paymentReservationInfo?.seatId) generateSplitReceiptBlob(paymentReservationInfo.seatId); setisCashPopupOpen(false); setSplitPaySeatTotal(undefined); setPaymentReservationInfo(undefined); fetchOrder(); setIsReceiptPopupOpen(true); }} seatTotal={splitPaySeatTotal} reservationInfo={paymentReservationInfo} onPaymentComplete={() => { autoPrintReceipt(); if (activeOrder && seats.length > 0) { recordMealConsumptionsForOrder(activeOrder); localStorage.removeItem(`seats_${tableId}`); } }} activeOrder={activeOrder ? { ...activeOrder, products: orderProducts.map(p => { let ms = 'none'; if (p.seatId && seatResults[p.seatId]) { const ev = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id); if (ev) ms = ev.status; } return { ...p, mealPlanStatus: ms }; }), subtotal: getSubtotal(), total: getTotal(), tax: getTax(), prostayReservationNumber: activeOrder.prostayReservationNumber || linkedReservationNumber, roomNumber: activeOrder.roomNumber || linkedRoomNumber } : activeOrder} />
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => setisCashPopupOpen(false)}></div>
                </>
            }

            {
                isPutOnRoomOpen &&
                <>
                    <SelectReservationPopup onClose={() => setIsPutOnRoomOpen(false)} onConfirmSelection={handleConfirmPutOnRoomSelection} order={activeOrder} />
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9997
                    }}></div>
                </>
            }

            {
                isCardPopupOpen &&
                <>
                    <CardPaymentPopup isOpen={isCardPopupOpen} onClose={() => { setisCardPopupOpen(false); setSplitPaySeatTotal(undefined); setPaymentReservationInfo(undefined); fetchOrder(); }} onPartialPayment={() => { if (paymentReservationInfo?.seatId) generateSplitReceiptBlob(paymentReservationInfo.seatId); setisCardPopupOpen(false); setSplitPaySeatTotal(undefined); setPaymentReservationInfo(undefined); fetchOrder(); setIsReceiptPopupOpen(true); }} seatTotal={splitPaySeatTotal} reservationInfo={paymentReservationInfo} onPaymentComplete={() => { autoPrintReceipt(); if (activeOrder && seats.length > 0) { recordMealConsumptionsForOrder(activeOrder); localStorage.removeItem(`seats_${tableId}`); } }} activeOrder={activeOrder ? { ...activeOrder, products: orderProducts.map(p => { let ms = 'none'; if (p.seatId && seatResults[p.seatId]) { const ev = seatResults[p.seatId].evaluations?.find(e => e.orderProductId === p.id); if (ev) ms = ev.status; } return { ...p, mealPlanStatus: ms }; }), subtotal: getSubtotal(), total: getTotal(), tax: getTax(), prostayReservationNumber: activeOrder.prostayReservationNumber || linkedReservationNumber, roomNumber: activeOrder.roomNumber || linkedRoomNumber } : activeOrder} />
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => setisCardPopupOpen(false)}></div>
                </>
            }

            {showWhoPaysPopup && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: 12, width: '90%', maxWidth: 400,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: 24,
                    }}>
                        <h5 style={{ margin: '0 0 16px', fontWeight: 600, color: '#101828' }}>Who is paying?</h5>
                        <p style={{ fontSize: 13, color: '#667085', margin: '0 0 16px' }}>Select the guest whose reservation will be charged.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {seats.filter(s => s.type === 'hotel_guest' && s.reservationId).map(seat => (
                                <button
                                    key={seat.id}
                                    onClick={() => {
                                        const resInfo = {
                                            reservationId: seat.reservationId!,
                                            reservationNumber: seat.reservationNumber!,
                                            roomNumber: seat.roomNumber!,
                                            roomWithStatusId: seat.roomWithStatusId!,
                                        };
                                        setPaymentReservationInfo(resInfo);
                                        setShowWhoPaysPopup(false);
                                        if (pendingPaymentMethod === 'cash') setisCashPopupOpen(true);
                                        else if (pendingPaymentMethod === 'card') setisCardPopupOpen(true);
                                        else if (pendingPaymentMethod === 'room') {
                                            // Charge full order to selected guest's room
                                            (async () => {
                                                if (!activeOrder) return;
                                                try {
                                                    setShowSpinner(true);
                                                    const orderTotal = getTotal();

                                                    const roomPayment = {
                                                        id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
                                                        amount: orderTotal,
                                                        type: 'room_charge',
                                                        date: new Date(),
                                                        change: 0,
                                                        balanceBefore: 0,
                                                        confirmationNumber: '',
                                                        cardType: '',
                                                        reservationId: seat.reservationId,
                                                        reservationNumber: seat.reservationNumber,
                                                        roomNumber: seat.roomNumber,
                                                        roomWithStatusId: seat.roomWithStatusId,
                                                        guestName: seat.guestName,
                                                    };
                                                    const updatedOrder = {
                                                        ...activeOrder,
                                                        payments: [...(activeOrder.payments || []), roomPayment],
                                                    };

                                                    const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/put-on-room`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({
                                                            reservationNumber: seat.reservationNumber,
                                                            order: { ...updatedOrder, total: orderTotal, roomNumber: seat.roomNumber, roomWithStatusId: seat.roomWithStatusId },
                                                        }),
                                                        credentials: 'include',
                                                    });

                                                    if (!response.ok) {
                                                        setShowSpinner(false);
                                                        const failure = await readRoomChargeFailure(response, 'Failed to charge to room');
                                                        setErrorTitle(failure.checkedOut ? GUEST_CHECKED_OUT_TITLE : 'Error');
                                                        setErrorContent(failure.message);
                                                        setShowError(true);
                                                        setTimeout(() => setShowError(false), failure.checkedOut ? GUEST_CHECKED_OUT_TOAST_MS : 3000);
                                                        return;
                                                    }

                                                    setActiveOrder(updatedOrder);
                                                    await saveOrder(updatedOrder);

                                                    setShowSpinner(false);
                                                    autoPrintReceipt(updatedOrder);
                                                    setSuccessTitle('Success!');
                                                    setSuccessContent(`Order charged to room ${seat.roomNumber} (${seat.guestName}).`);
                                                    setShowSuccess(true);
                                                    setTimeout(() => { setShowSuccess(false); navigate('/orders'); }, 3000);
                                                } catch (error: any) {
                                                    setShowSpinner(false);
                                                    setErrorTitle('Error');
                                                    setErrorContent(error.message || 'Something went wrong.');
                                                    setShowError(true);
                                                }
                                            })();
                                        }
                                        setPendingPaymentMethod(null);
                                    }}
                                    style={{
                                        padding: '12px 16px', borderRadius: 8, border: '1px solid #EAECF0',
                                        backgroundColor: '#F0F4FF', cursor: 'pointer', textAlign: 'left',
                                    }}
                                >
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#344054' }}>
                                        {seat.guestName}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#667085' }}>
                                        Room {seat.roomNumber} - Booking #{seat.reservationNumber}
                                    </div>
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => { setShowWhoPaysPopup(false); setPendingPaymentMethod(null); }}
                            style={{
                                marginTop: 16, width: '100%', padding: '10px', borderRadius: 8,
                                border: '1px solid #D0D5DD', backgroundColor: '#fff',
                                cursor: 'pointer', fontWeight: 500, color: '#344054',
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {
                selectedProductToAdd != null &&
                <>
                    {selectedProductToAdd.variants && selectedProductToAdd.variants.length > 0 && isAddVariantOpen &&
                        <AddVariantsPopup product={selectedProductToAdd} onClose={() => setIsAddVariantOpen(false)} onVariantSelected={handleVariantSelected} onNext={handleOnNextInVariant} />
                    }
                    {
                        isAddAddonOpen && selectedProductToAdd.addons && selectedProductToAdd.addons.length > 0 &&
                        <AddAddonsPopup product={selectedProductToAdd} onClose={() => setIsAddAddonOpen(false)} onNext={handleSaveAddons} />
                    }
                    {
                        (isAddAddonOpen || isAddVariantOpen) &&
                        <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={handleOverlayClick}></div>
                    }
                </>
            }

            {
                isTableActionsPopupOpen &&
                <TableActionsMenu
                    anchorRef={tableActionsBtnRef}
                    tableLabel={activeOrder?.tableName ? `Table ${activeOrder.tableName}` : undefined}
                    onClose={() => setIsTableActionsPopupOpen(false)}
                    onAddDiscount={handleAddDiscount}
                    onChangeTable={handleChangeTables}
                    onMergeTables={handleMergeTables}
                    onDeleteOrder={handleDeleteOrderClick}
                    hasPerm={hasPerm}
                />
            }

            {
                showDeleteOrderConfirmation &&
                <>
                    <div className='popup-overlay' onClick={() => setShowDeleteOrderConfirmation(false)}></div>
                    <DeleteConfirmation
                        title='Delete Order'
                        content='Are you sure you want to delete this order? This action cannot be undone.'
                        onClose={() => setShowDeleteOrderConfirmation(false)}
                        onDelete={handleDeleteOrder}
                    />
                </>
            }

            {
                isAddDiscountPopupOpen &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => setIsAddDiscountPopupOpen(false)}></div>
                    <AddDiscountPopup onSelectedOrderDiscount={handleSelectedOrderDiscount} onClose={() => setIsAddDiscountPopupOpen(false)} orderProducts={orderProducts} onSelectedProductWithAmount={handleSelectedProductAmountDiscount} />
                </>
            }
            {
                isAddDiscountValuePopupOpen &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => closeAddDiscountValuePopup()}></div>
                    <AddDiscountValue onClose={closeAddDiscountValuePopup} product={addingDiscountForProduct} onDiscountSubmit={handleDiscountValueSaved} />
                </>
            }
            {
                addingDiscountForCombination &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => closeAddDiscountForCombination()}></div>
                    <AddCombinationDiscountPopup onClose={closeAddDiscountForCombination} combination={addingDiscountForCombination} onDiscountSubmit={handleCombinationDiscountValueSaved} />
                </>
            }
            {
                isAddNotePopupOpen &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => closeAddNoteForProduct()}></div>
                    <AddProductNotePopup existingNote={undefined} onClose={() => closeAddNoteForProduct()} onNoteSubmit={handleSaveNote} />
                </>
            }
            {
                addingAddonForProduct &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => closeAddNoteForProduct()}></div>
                    <EditAddonsForProduct onClose={() => closeAddAddonForProduct()} onSave={handleSaveAddonsForProduct} orderProduct={addingAddonForProduct} product={products.find(x => x.id == addingAddonForProduct.product.id)} />
                </>
            }
            {
                addingVariantForProduct &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => closeAddVariantForProduct()}></div>
                    <EditVariantsForProduct onClose={() => closeAddVariantForProduct()} onSave={handleSaveVariantForProduct} orderProduct={addingVariantForProduct} product={products.find(x => x.id == addingVariantForProduct.product.id)} />
                </>
            }
            {
                isOrderDiscountOpen &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => setisOrderDiscountOpen(false)}></div>
                    <AddOrderDiscountValue onClose={() => setisOrderDiscountOpen(false)} order={activeOrder} onDiscountSubmit={handleSaveOrderDiscountValue} />
                </>
            }
            {(showSpinner || catalogLoading || orderLoading) && (
                <Spinner />
            )}
            {showSuccess && <SuccessNotification headerTitle={successTitle} content={successContent} />}
            {showError && <ErrorNotification headerTitle={errorTitle} content={errorContent} />}
            {orderClosedInfo && (
                <OrderClosedPopup
                    closedBy={orderClosedInfo.closedBy}
                    closedAt={orderClosedInfo.closedAt}
                    onReturnToTables={() => { setOrderClosedInfo(null); navigate('/tables'); }}
                />
            )}
            {
                isChangeTablePopupOpen &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => setIsChangeTablePopupOpen(false)}></div>
                    <ChangeTablePopup onClose={() => setIsChangeTablePopupOpen(false)} tables={emptyTables} onChangeTable={handleChangeTableSave} currentTable={activeOrder?.tableName || ''} />
                </>
            }
            {
                isChangeTableConfirmationOpen &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => setIsChangeTablePopupOpen(false)}></div>
                    <ChangeTableConfirmationPopup onClose={() => setIsChangeTableConfirmationOpen(false)} onConfirm={handleChangeTableConfirmation} currentTable={activeOrder?.tableName || ''} newTable={selectedNewTableName} />
                </>
            }
            {isSendProductsPopupOpen &&
                <SendProductsPopup
                    onClose={() => setIsSendProductsPopupOpen(false)}
                    order={activeOrder}
                    floor={floor}
                    products={products}
                    combinations={combinations}
                    onSent={() => {
                        // The popup shows its own success/reprint screen; just close + refresh.
                        setIsSendProductsPopupOpen(false);
                        fetchOrder();
                    }}
                />
            }
            {
                isDiscountPopupOpenForItems &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => setIsDiscountPopupOpenForItems(false)}></div>
                    <AddDiscountForOrderItem onClose={() => setIsDiscountPopupOpenForItems(false)} onDiscountSubmit={handleAddDiscountToItems} discountAmount={undefined} discountType={undefined} />

                </>
            }
            {
                (editingDiscountForProduct || editingDiscountForCombination) &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => { setEditingDiscountForCombination(null); setEditingDiscountForProduct(null); }}></div>
                    <AddDiscountForOrderItem onClose={() => { setEditingDiscountForCombination(null); setEditingDiscountForProduct(null); }} onDiscountSubmit={handleSaveEditDiscount} discountAmount={editingDiscountForCombination ? editingDiscountForCombination.discount : editingDiscountForProduct?.discount} discountType={editingDiscountForCombination ? editingDiscountForCombination.discountType : editingDiscountForProduct?.discountType} discountName={editingDiscountForCombination ? editingDiscountForCombination.discountName : editingDiscountForProduct?.discountName} />
                </>
            }
            {
                (editingItemProduct || editingItemCombination) &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => { setEditingItemCombination(null); setEditingItemProduct(null) }}></div>
                    <EditOrderItemPopup onClose={() => { setEditingItemCombination(null); setEditingItemProduct(null) }} orderCombination={editingItemCombination} orderProduct={editingItemProduct}
                        onEditNote={handleEditNote} onEditAddon={handleEditAddons} onEditVariant={handleEditVariants} onEditDiscount={handleEditDiscount} onEditProductsForFlexibleCombination={handleEditingProductsForFlexibleComb} />
                </>
            }

            {
                (editingNoteForCombination || editingNoteForProduct) &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => closeAddNoteForProduct()}></div>
                    <AddProductNotePopup existingNote={editingNoteForProduct ? editingNoteForProduct.note : editingNoteForCombination?.note} onClose={() => closeAddNoteForProduct()} onNoteSubmit={handleSaveEditNote} />
                </>
            }

            {
                (deletingFromItemCombination || deletingFromItemProduct) &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => { setdeletingFromItemCombination(null); setdeletingFromItemProduct(null) }}></div>
                    <DeleteFromOrderItem onClose={() => { setdeletingFromItemCombination(null); setdeletingFromItemProduct(null) }} orderCombination={deletingFromItemCombination} orderProduct={deletingFromItemProduct}
                        onRemoveNote={handleDeleteNote} onRemoveAddon={handleDeleteAddons} onRemoveVariant={handleDeleteVariants} onRemoveDiscount={handleDeleteDiscount} />
                </>
            }
            {
                addingFlexibleCombination != null &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => { setAddingFlexibleCombination(null); }}></div>
                    <AddFlexibleCombinationPopup onClose={() => { setAddingFlexibleCombination(null); }} combination={addingFlexibleCombination} onSave={handleSaveFlexibleCombinationOnOrder} />
                </>
            }
            {
                isEditingProductsForCombination != null &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height }} onClick={() => { setAddingFlexibleCombination(null); }}></div>
                    <EditFlexibleCombinationPopup combination={getCombinationBasedOnOrderCombinationId(isEditingProductsForCombination.combinationId)} onClose={() => { setIsEditingProductsForCombination(null); }} existingOrderCombination={isEditingProductsForCombination} onSave={handleUpdatingFlexibleCombinationOnOrder} />
                </>
            }
            {/* <div className="search-container col-5 d-flex justify-content-center" onClick={() => { if (!searchMenuOpen) toggleSearchMenu() }} ref={searchRef} style={{ position: 'fixed', zIndex: 9, top: 9, left: '50%', transform: 'translateX(-50%)', }}>
                <svg
                    className="search-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M26.25 26.25L20.8213 20.8213M20.8213 20.8213C21.7499 19.8927 22.4865 18.7902 22.989 17.5769C23.4916 16.3637 23.7503 15.0633 23.7503 13.75C23.7503 12.4368 23.4916 11.1364 22.989 9.92308C22.4865 8.70979 21.7499 7.60737 20.8213 6.67876C19.8927 5.75015 18.7902 5.01354 17.5769 4.51098C16.3637 4.00842 15.0633 3.74976 13.75 3.74976C12.4368 3.74976 11.1364 4.00842 9.92308 4.51098C8.70979 5.01354 7.60737 5.75015 6.67876 6.67876C4.80335 8.55418 3.74976 11.0978 3.74976 13.75C3.74976 16.4022 4.80335 18.9459 6.67876 20.8213C8.55418 22.6967 11.0978 23.7503 13.75 23.7503C16.4022 23.7503 18.9459 22.6967 20.8213 20.8213Z"
                        stroke="#222222"
                        strokeWidth="1.875"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                <input
                    type="text"
                    placeholder="Search by guest name or room number"
                    value={searchGuestTerm}
                    onClick={() => { if (!searchMenuOpen) setSearchMenuOpen(true) }}
                    onChange={handleGuestSearchChange}
                    className="search-input"
                />
                {
                    searchMenuOpen &&
                    <>
                        {
                            searchingGuests ?
                                <>
                                    <div className='search-menu p-3' style={{ textAlign: 'center', position: 'fixed', zIndex: 9, top: 35, backgroundColor: '#fff', width: 'calc(100% - 28px)' }}>
                                        <div className="search-spinner"></div>
                                    </div>
                                </>
                                :
                                <>
                                    {
                                        searchResults.length > 0 ? (
                                            <div className='search-menu' style={{ position: 'fixed', zIndex: 9, top: 35, backgroundColor: '#fff', width: 'calc(100% - 28px)' }}>
                                                <small style={{ paddingInline: 10, fontSize: 12, fontFamily: 'Inter' }}>All results for "{searchGuestTerm}":</small>
                                                <div className='results-container'>
                                                    {
                                                        searchResults.map((result) => {
                                                            return (
                                                                <div className='search-result' onClick={() => handleAddCustomerToOrder(result.email || "", result.firstName + " " + result.lastName, result.roomNumber || "")}>
                                                                    <div className='d-flex justify-content-between align-items-start'>
                                                                        <div className='d-flex gap-1 align-items-end'>
                                                                            <span style={{ color: '#1643e0', fontWeight: 600, fontSize: 14 }}>{result.firstName} {result.lastName}</span>
                                                                            <span style={{ fontSize: 12 }}>{result.reservationNumber} {result.roomNumber ? "-  " + result.roomNumber : ""} </span>
                                                                        </div>
                                                                        <div className='col d-flex flex-column align-items-end'>
                                                                            <small>{formatDate(result.reservationCheckinDate || "")} - {formatDate(result.reservationCheckoutDate || "")}</small>
                                                                            <small style={{ color: getStatusColor(result.reservationStatus || ""), fontWeight: 500 }}>{result.reservationStatus}</small>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })
                                                    }
                                                </div>
                                            </div>
                                        )
                                            :
                                            (
                                                <>
                                                    <div className='search-menu p-3' style={{ textAlign: 'center', position: 'fixed', zIndex: 9, top: 35, backgroundColor: '#fff', width: 'calc(100% - 28px)' }}>
                                                        <div style={{ color: '#344054', fontFamily: '"Inter", sans-serif', fontSize: '14px' }}>Type to see search results</div>
                                                        <p style={{ color: '#98A2B3' }}>Search guest names or email addresses</p>
                                                    </div>
                                                </>
                                            )
                                    }
                                </>
                        }
                    </>
                }

            </div> */}
            <Toolbar
                onSelectGuest={handleAddCustomerToOrder}
                isSearchMenuOpen={searchMenuOpen}
                orderGuests={seats.length}
                orderServer={activeOrder?.waiter}
                orderTime={activeOrder?.date ? new Date(activeOrder.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '10:26'}
            />

            {/* Table Setup Dialog */}
            {showTableSetup && floor && (
                <TableSetupDialog
                    tableName={(() => {
                        const table = floor.tables.find(t => t.id === tableId);
                        return table ? `Table ${table.tableNo}` : 'Table';
                    })()}
                    defaultSeats={(() => {
                        // A booking's party size beats the table's seat count.
                        if (seatingHandoff?.partySize) return seatingHandoff.partySize;
                        const table = floor.tables.find(t => t.id === tableId);
                        return table?.seats || 2;
                    })()}
                    existingSeats={seats.length > 0 ? seats : undefined}
                    contextNote={seatingHandoff ? `Seating reservation ${seatingHandoff.code}` : undefined}
                    pendingGuest={pendingDialogGuest || seatingPendingGuest}
                    onPendingGuestConsumed={() => setPendingDialogGuest(null)}
                    onConfirm={handleTableSetupConfirm}
                    onCancel={handleTableSetupCancel}
                />
            )}

            {/* Extra Charge Warning Toast */}
            {extraWarning && (
                <ExtraWarning
                    message={extraWarning.message}
                    roomNumber={extraWarning.roomNumber}
                    onDismiss={() => setExtraWarning(null)}
                />
            )}

            {pendingOverrideProduct && (
                <MealPlanOverridePopup
                    productName={pendingOverrideProduct.product.name}
                    extraReason={pendingOverrideProduct.extraReason}
                    guestName={pendingOverrideProduct.guestName}
                    roomNumber={pendingOverrideProduct.roomNumber}
                    onCharge={handleMealPlanOverrideCharge}
                    onWaive={handleMealPlanOverrideWaive}
                    onCancel={handleMealPlanOverrideCancel}
                />
            )}
        </div>
    );
};



export default TableOrder;

